import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  InputAdornment,
  Paper,
  Collapse,
  Alert,
  Tooltip,
  Tab,
  Tabs,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Switch,
  Divider,
  MenuItem,
  Snackbar,
  useMediaQuery,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EmailIcon from '@mui/icons-material/Email';
import ExtensionIcon from '@mui/icons-material/Extension';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import TagIcon from '@mui/icons-material/Tag';
import PlaceIcon from '@mui/icons-material/Place';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SellIcon from '@mui/icons-material/Sell';
import TranslateIcon from '@mui/icons-material/Translate';
import HelpButton from '../shared/HelpButton';
import DecimalField from '../shared/DecimalField';
import InventoryTrackingFields from '../parts/InventoryTrackingFields';
import DuplicatePartResolver from './DuplicatePartResolver';
import FilePickerButton from '../shared/FilePickerButton';
import SupplierPartsTab from './SupplierPartsTab';
import BarcodeScannerDialog from './BarcodeScannerDialog';
import CatalogueStockReconcileDialog from './CatalogueStockReconcileDialog';
import { canScanBarcodes } from '../../utils/barcodeDetector';
import { usePartCodeOnly } from '../../utils/partSearchPrefs';
import { getOrganizationId, useUserStore } from '../../store';
import { DEFAULT_VAT_RATE, calcPriceExclVat, calcPriceInclVat, formatPrice, formatPriceWithCurrency } from '../../utils/vat';
import { formatQuantity, formatDate } from '../../utils';
import { UNIT_OPTIONS } from '../../utils/units';
import { useServerSearch } from '../../hooks/useServerSearch';

import { can } from '../../utils/access';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';
const POLL_INTERVAL = 5000; // 5 seconds

function AddPartDialog({
  open, handleClose, setMaintenance, maintenance, mode = 'maintenance', onPartCreated, manualOnly = false,
  // Pre-filled search term (e.g. the code of a deleted part being replaced); searched on open.
  initialSearch = '',
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled ?? false;
  const canEditPrices = can(currentOrganization, 'work_orders.edit_prices');
  const canSeeCosts = can(currentOrganization, 'costs.view_purchase_prices');
  // Barcode scanning: show on touch devices (phones/tablets) that have a camera.
  // The ponyfill covers iOS/WebKit, so no org feature flag is needed anymore.
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const showBarcodeScanner = isTouchDevice && canScanBarcodes();

  // Inventory filter state
  const [onlyTrackedParts, setOnlyTrackedParts] = useState(false);
  // Code-only search: a workshop-wide preference shared with the other part
  // pickers and the Osat/Varasto lists (see utils/partSearchPrefs).
  const [codeOnly, setCodeOnly] = usePartCodeOnly();

  const searchExtraParams = useMemo(() => ({
    ...(onlyTrackedParts ? { has_inventory: true } : {}),
    // Inventory mode lists parts to START tracking for, so tracked ones are noise.
    ...(mode === 'inventory' ? { has_inventory: false } : {}),
    ...(codeOnly ? { code_only: true } : {}),
  }), [onlyTrackedParts, codeOnly, mode]);

  const isMaintenanceMode = mode === 'maintenance';
  const isRegistryMode = mode === 'registry';
  // Template mode: used by the service package editor. Same UX as maintenance
  // (pick existing from registry OR create new), but skips the maintenance POST
  // and returns the picked/created Part to the parent via onPartCreated.
  const isTemplateMode = mode === 'template';
  // Inventory mode: the Varasto view's "Lisää osa". Same picker as maintenance
  // mode (registry + vendor catalogues, already-tracked parts filtered out), but
  // a pick starts inventory tracking for the Part (opening balance, shelf
  // location, reorder point) instead of adding it to a work order. The manual
  // form stays behind "Luo uusi osa käsin" with tracking on by default; the
  // import tools are hidden as in manualOnly.
  const isInventoryMode = mode === 'inventory';
  const showRegistryPicker = isMaintenanceMode || isTemplateMode || isInventoryMode;
  const hideImportTools = manualOnly || isInventoryMode;
  // Vendor catalogue matches are folded into the "Lisää osa" search box (no
  // separate tab) when the feature is enabled for the org.
  const catalogueEnabled = currentOrganization?.vendor_catalogues_enabled ?? false;
  const suggestionsTabIndex = isMaintenanceMode ? 2 : 1;

  // Server-side search for parts catalog
  const {
    items: parts,
    loading: partsLoading,
    autocompleteProps: partsAutocompleteProps,
    reset: resetPartsSearch,
    setSearchInput: triggerPartsSearch,
    // minSearchLength 3: a 1–2 char substring matches almost everything and
    // cannot use the trigram indexes (pg_trgm needs 3-char grams), so it would
    // force a slow sequential scan on large catalogs (>1M parts).
  } = useServerSearch('/api/parts/', { loadOnMount: false, minSearchLength: 3, extraParams: searchExtraParams });

  // Registry state
  const [error, setError] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Vendor-catalogue matches folded into the same search box (flag-gated).
  const [catalogueResults, setCatalogueResults] = useState([]);
  const [cataloguePicking, setCataloguePicking] = useState(false);
  const [reconcile, setReconcile] = useState(null);
  const catalogueDebounceRef = useRef(null);
  // Pre-filled search (initialSearch): the input element and the term still pending.
  const partSearchInputRef = useRef(null);
  const prefillRef = useRef('');
  // Searchable vendor catalogues for the filter chips, and the selected subset.
  // When any are selected, the search is restricted to those catalogues only.
  const [catalogueSources, setCatalogueSources] = useState([]);
  const [selectedCatalogueIds, setSelectedCatalogueIds] = useState([]);
  const catalogueFilterActive = selectedCatalogueIds.length > 0;
  // "Vain varastoseuratut" means exactly that: a hinnasto row is a part the
  // workshop does not stock, so catalogue results are hidden while the chip is
  // on (same rule the invoice / kassamyynti picker already applies). Catalogue
  // filter chips take precedence: they hide the stock chip and scope the search
  // to the chosen catalogues.
  const shelfActive = inventoryEnabled && onlyTrackedParts && !catalogueFilterActive;
  const catalogueActive = catalogueEnabled && !shelfActive;

  // Barcode scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [barcodeSnackbar, setBarcodeSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [partSearchInput, setPartSearchInput] = useState('');

  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [showManualForm, setShowManualForm] = useState(manualOnly);
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  // Manual form state
  const [newPart, setNewPart] = useState({
    name: '',
    unit: 'kpl',
    description: '',
    price: '',
    sell_price: '',
    manufacturer_code: '',
    manufacturer_name: '',
    quantity: '',
    is_margin_vat_scheme: false,
    margin_vat_purchase_price: '',
  });
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const [priceIncludesVat, setPriceIncludesVat] = useState(orgDefaultVat);
  const [sellPriceIncludesVat, setSellPriceIncludesVat] = useState(orgDefaultVat);

  // Inventory tracking state (manual form)
  const [enableTracking, setEnableTracking] = useState(manualOnly || isInventoryMode);
  // Inventory mode: tracking fields for the PICKED part, separate from the
  // manual form's own fields (both can be open at once).
  const [trackInitialQuantity, setTrackInitialQuantity] = useState('0');
  const [trackShelfLocation, setTrackShelfLocation] = useState('');
  const [trackReorderPoint, setTrackReorderPoint] = useState('');
  const [initialQuantity, setInitialQuantity] = useState('0');
  const [shelfLocation, setShelfLocation] = useState('');

  // Pending suggestions state (combined email + extension)
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [processingIds, setProcessingIds] = useState(new Set());
  const [expandedSuggestions, setExpandedSuggestions] = useState({});
  const pollIntervalRef = useRef(null);
  // Track user edits to preserve them during polling
  const userEditsRef = useRef({});

  // Paste/extraction state
  const [pasteContent, setPasteContent] = useState('');
  const [pastedImage, setPastedImage] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractedParts, setExtractedParts] = useState([]);
  const [expandedExtractedParts, setExpandedExtractedParts] = useState({});
  const [eventId, setEventId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterByVehicle, setFilterByVehicle] = useState(false);
  const [filterSuggestionsByVehicle, setFilterSuggestionsByVehicle] = useState(false);

  // Batch action states for extracted parts
  const [vatSwapped, setVatSwapped] = useState(false);
  const [priceIsSellPrice, setPriceIsSellPrice] = useState(false);
  const [translatingNames, setTranslatingNames] = useState(false);
  const [namesTranslated, setNamesTranslated] = useState(false);

  // Settings state
  const [inboxEmail, setInboxEmail] = useState('');
  const [copied, setCopied] = useState(false);

  // Track added parts during this session
  const [addedPartsCount, setAddedPartsCount] = useState(0);
  const [addedPartIds, setAddedPartIds] = useState([]);

  // Cancel confirmation dialog
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Awaiting confirmation
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);

  // Hide from parts registry (maintenance-only part)
  const [hideFromRegistry, setHideFromRegistry] = useState(false);

  // Duplicate detection state
  const [extractedConflicts, setExtractedConflicts] = useState([]);
  const [exactMatchCount, setExactMatchCount] = useState(0);
  const [suggestionConflict, setSuggestionConflict] = useState(null);
  const [resolvingConflict, setResolvingConflict] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // Helper functions
  // ─────────────────────────────────────────────────────────────

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const isUrl = (text) => {
    const trimmed = text.trim();
    return /^https?:\/\//i.test(trimmed) && !trimmed.includes('\n');
  };

  // Find new MaintenancePart IDs by comparing old and new parts arrays
  const findNewPartIds = (oldParts, newParts) => {
    const oldIds = new Set((oldParts || []).map((p) => p.id));
    return (newParts || []).filter((p) => !oldIds.has(p.id)).map((p) => p.id);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'success.main';
    if (confidence >= 0.5) return 'warning.main';
    return 'error.main';
  };

  // Group suggestions by their source event (email subject or page title)
  const groupSuggestionsByEvent = (suggestions) => {
    const groups = {};
    suggestions.forEach((suggestion) => {
      const key = `${suggestion.sourceType}-${suggestion.eventSubject || 'Unknown'}`;
      if (!groups[key]) {
        groups[key] = {
          sourceType: suggestion.sourceType,
          eventSubject: suggestion.eventSubject || t('Unknown source'),
          sourceLabel: suggestion.sourceLabel,
          suggestions: [],
        };
      }
      groups[key].suggestions.push(suggestion);
    });
    return Object.values(groups);
  };

  // ─────────────────────────────────────────────────────────────
  // Vehicle-based filtering for extracted parts
  // ─────────────────────────────────────────────────────────────

  const vehicleRegNumber = maintenance?.vehicle?.registration_number || '';
  const vehicleVin = maintenance?.vehicle?.vin || '';

  const fuzzyVehicleMatch = (extracted, actual) => {
    if (!extracted || !actual) return false;
    const a = extracted.toUpperCase().replace(/[-\s]/g, '');
    const b = actual.toUpperCase().replace(/[-\s]/g, '');
    if (a === b) return true;
    // Levenshtein distance for fuzzy OCR tolerance
    if (Math.abs(a.length - b.length) > 2) return false;
    const maxDist = a.length <= 8 ? 1 : 2;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      const curr = [i];
      for (let j = 1; j <= b.length; j++) {
        curr[j] = a[i - 1] === b[j - 1]
          ? prev[j - 1]
          : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
      }
      prev = curr;
    }
    return prev[b.length] <= maxDist;
  };

  const displayedPartsWithIndex = useMemo(() => {
    const items = extractedParts.map((part, index) => ({ part, originalIndex: index }));
    if (!filterByVehicle) return items;
    return items.filter(({ part }) => {
      const partReg = part.vehicle_registration_number || '';
      const partVin = part.vehicle_vin || '';
      // Parts with no vehicle info are always shown (can't determine their vehicle)
      if (!partReg && !partVin) return true;
      // Check if any vehicle identifier matches
      if (partReg && vehicleRegNumber && fuzzyVehicleMatch(partReg, vehicleRegNumber)) return true;
      if (partVin && vehicleVin && fuzzyVehicleMatch(partVin, vehicleVin)) return true;
      return false;
    });
  }, [extractedParts, filterByVehicle, vehicleRegNumber, vehicleVin]);

  // Check if filter checkbox should be shown
  const hasVehicleIdentifier = !!(vehicleRegNumber || vehicleVin);
  const hasPartsWithVehicleInfo = extractedParts.some(
    (p) => p.vehicle_registration_number || p.vehicle_vin
  );

  // ─────────────────────────────────────────────────────────────
  // Vehicle-based filtering for suggestions (email + extension)
  // ─────────────────────────────────────────────────────────────

  const filteredSuggestions = useMemo(() => {
    if (!filterSuggestionsByVehicle) return pendingSuggestions;
    return pendingSuggestions.filter((s) => {
      const partReg = s.vehicle_registration_number || '';
      const partVin = s.vehicle_vin || '';
      if (!partReg && !partVin) return true;
      if (partReg && vehicleRegNumber && fuzzyVehicleMatch(partReg, vehicleRegNumber)) return true;
      if (partVin && vehicleVin && fuzzyVehicleMatch(partVin, vehicleVin)) return true;
      return false;
    });
  }, [pendingSuggestions, filterSuggestionsByVehicle, vehicleRegNumber, vehicleVin]);

  const suggestionGroups = groupSuggestionsByEvent(filteredSuggestions);

  const hasSuggestionsWithVehicleInfo = pendingSuggestions.some(
    (s) => s.vehicle_registration_number || s.vehicle_vin
  );

  // ─────────────────────────────────────────────────────────────
  // Fetch pending suggestions (combined email + extension)
  // ─────────────────────────────────────────────────────────────

  const fetchPendingSuggestions = useCallback(async () => {
    const flattenSuggestions = (events, sourceType, translator) => {
      const suggestions = [];
      events.forEach((event) => {
        event.suggestions
          .filter((s) => s.status === 'pending')
          .forEach((suggestion) => {
            suggestions.push({
              ...suggestion,
              sourceType,
              sourceLabel:
                sourceType === 'email'
                  ? event.sender_email || translator('Email')
                  : translator('Browser extension'),
              eventSubject: event.subject || event.source_title || '',
            });
          });
      });
      return suggestions;
    };

    try {
      const [emailRes, extensionRes] = await Promise.all([
        axios.get('/api/parts/email-suggestions/'),
        axios.get('/api/parts/extension-suggestions/'),
      ]);

      const combined = [
        ...flattenSuggestions(emailRes.data, 'email', t),
        ...flattenSuggestions(extensionRes.data, 'extension', t),
      ];

      // Merge with user edits to preserve local changes
      const mergedSuggestions = combined.map((suggestion) => {
        const edits = userEditsRef.current[suggestion.id];
        if (edits) {
          return { ...suggestion, ...edits };
        }
        return suggestion;
      });

      setPendingSuggestions(mergedSuggestions);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  }, [t]);

  // ─────────────────────────────────────────────────────────────
  // Initial data fetch and polling
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      // Reset all state
      setSelectedPart(null);
      setAwaitingConfirmation(false);
      setHideFromRegistry(false);
      setQuantity('');
      setError(null);
      setNewPart({
        name: '',
        unit: 'kpl',
        description: '',
        price: '',
        sell_price: '',
        manufacturer_code: '',
        manufacturer_name: '',
        quantity: '',
        is_margin_vat_scheme: false,
        margin_vat_purchase_price: '',
      });
      setPriceIncludesVat(orgDefaultVat);
      setSellPriceIncludesVat(orgDefaultVat);
      // Registry mode (pure add-to-registry from /parts or /inventory) opens
      // straight into the manual form — pick-existing makes no sense there.
      // Template mode starts on the picker, matching maintenance mode.
      setShowManualForm(isRegistryMode || manualOnly);
      setEnableTracking(manualOnly || isInventoryMode);
      setTrackInitialQuantity('0');
      setTrackShelfLocation('');
      setTrackReorderPoint('');
      setInitialQuantity('0');
      setShelfLocation('');
      setActiveTab(0);
      setPasteContent('');
      setPastedImage(null);
      setExtractedParts([]);
      setEventId(null);
      setAddedPartsCount(0);
      setAddedPartIds([]);
      setExactMatchCount(0);
      setExtractedConflicts([]);
      setSuggestionConflict(null);
      setOnlyTrackedParts(false);
      setPartSearchInput('');
      setCatalogueResults([]);
      setSelectedCatalogueIds([]);
      if (catalogueDebounceRef.current) clearTimeout(catalogueDebounceRef.current);
      setScannerOpen(false);
      setFilterByVehicle(false);
      setFilterSuggestionsByVehicle(false);
      // Clear user edits tracking
      userEditsRef.current = {};

      // Reset parts search and load initial parts
      resetPartsSearch();

      // Fetch other initial data
      const fetchInitialData = async () => {
        try {
          const inboxRes = await axios.get('/api/parts/inbox-email/');
          setInboxEmail(inboxRes.data.inbox_email);
          // Also fetch pending suggestions
          await fetchPendingSuggestions();
        } catch (err) {
          setError(t('Failed to load data. Please try again.'));
        }
      };

      fetchInitialData();

      // Load searchable vendor catalogues for the filter chips (best-effort).
      setCatalogueSources([]);
      if (catalogueEnabled) {
        axios
          .get('/api/catalogue-search/sources/')
          .then((res) => setCatalogueSources(Array.isArray(res.data) ? res.data : []))
          .catch(() => setCatalogueSources([]));
      }

      // Start polling for suggestions
      pollIntervalRef.current = setInterval(() => {
        fetchPendingSuggestions();
      }, POLL_INTERVAL);
    } else {
      // Stop polling when dialog closes
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [open, fetchPendingSuggestions]);

  // Re-fetch parts when the search scope changes (inventory filter or code-only)
  useEffect(() => {
    if (open) {
      resetPartsSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyTrackedParts, codeOnly]);

  // The catalogue half is a separate request; re-scope it on the same flips
  // (code-only changes the query, stock-only hides catalogue rows entirely).
  useEffect(() => {
    if (open) {
      runCatalogueSearch(partSearchInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeOnly, onlyTrackedParts]);

  // Re-run the catalogue search when the catalogue filter chips change, so
  // toggling a vendor immediately re-scopes the visible results.
  useEffect(() => {
    if (open) {
      runCatalogueSearch(partSearchInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatalogueIds]);

  // Opened with a pre-filled term (replacing a deleted part): search both halves at once.
  // Runs after the open-reset effect above, so the term survives the reset. The field is
  // focused first: MUI Autocomplete resets an unfocused input back to its (null) value's
  // label, which would blank the term on the next render; prefillRef guards that reset.
  useEffect(() => {
    if (open && initialSearch) {
      prefillRef.current = initialSearch;
      partSearchInputRef.current?.focus();
      setPartSearchInput(initialSearch);
      triggerPartsSearch(initialSearch);
      runCatalogueSearch(initialSearch);
    } else {
      prefillRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSearch]);

  // ─────────────────────────────────────────────────────────────
  // Registry part handlers
  // ─────────────────────────────────────────────────────────────

  // Vendor-catalogue search: runs in parallel with the local Part search on the
  // same debounced input, never blocking it. Failures are non-fatal (registry
  // results still render). Mirrors useServerSearch's 300ms / min-length 3.
  const runCatalogueSearch = useCallback(
    (query) => {
      if (!catalogueActive) {
        setCatalogueResults([]);
        return;
      }
      if (catalogueDebounceRef.current) clearTimeout(catalogueDebounceRef.current);
      const q = (query || '').trim();
      const filterActive = selectedCatalogueIds.length > 0;
      // With a catalogue filter active, an empty/short query browses that catalogue
      // (so clicking shows a preview, like the registry list does in the default view).
      // Below 3 chars the backend resolver (MIN_QUERY_LENGTH) returns nothing for an
      // unscoped search and the trigram index can't be used, so don't fire the request.
      if (q.length < 3 && !filterActive) {
        setCatalogueResults([]);
        return;
      }
      catalogueDebounceRef.current = setTimeout(async () => {
        try {
          const params = { q };
          // When filter chips are selected, restrict the search to those catalogues.
          if (filterActive) params.catalogues = selectedCatalogueIds.join(',');
          if (codeOnly) params.code_only = true;
          const res = await axios.get('/api/catalogue-search/', { params });
          setCatalogueResults(res.data?.results || []);
        } catch {
          setCatalogueResults([]);
        }
      }, 300);
    },
    [catalogueActive, selectedCatalogueIds, codeOnly]
  );

  // Catalogue rows shown in the same list as registry Parts, after them, and
  // suppressing any catalogue row already linked to a shown registry Part.
  const combinedOptions = useMemo(() => {
    if (!catalogueActive) return parts;
    const catalogueOptions = catalogueResults.map((c) => ({ ...c, __catalogue: true, id: `__cat_${c.item_id}` }));
    // Filter chips active → show ONLY the selected catalogues' items (registry hidden).
    if (catalogueFilterActive) return catalogueOptions;
    if (catalogueResults.length === 0) return parts;
    // Default → registry parts first, then catalogue rows, suppressing any catalogue
    // row already linked to a shown registry Part.
    const linked = new Set(
      (parts || [])
        .filter((p) => p.source_catalogue)
        .map((p) => `${p.source_catalogue}|${p.manufacturer_code || ''}|${p.source_brand || ''}`)
    );
    const deduped = catalogueOptions.filter(
      (c) => !linked.has(`${c.catalogue_id}|${c.source_code || ''}|${c.brand || ''}`)
    );
    return [...(parts || []), ...deduped];
  }, [catalogueActive, catalogueFilterActive, parts, catalogueResults]);

  // Turn a picked catalogue row into a Part, then feed it into the exact same
  // "selected part" path a local pick uses (select → set quantity → Add).
  const selectCatalogueItem = async (option) => {
    setCataloguePicking(true);
    setError(null);
    try {
      let part;
      if (hideFromRegistry && isMaintenanceMode) {
        // "Älä lisää varaosarekisteriin": mirror the local hidden-create flow —
        // a maintenance-only Part, not added to the visible registry.
        const createRes = await axios.post('/api/parts/', {
          name: option.name || option.source_code,
          manufacturer_code: option.source_code,
          manufacturer_name: option.brand || '',
          price: option.purchase_price,
          vat_rate: DEFAULT_VAT_RATE,
          source_type: 'catalogue',
          hide_from_registry: true,
        });
        part = createRes.data;
      } else {
        // Default: if the workshop already stocks this code (inventory-tracked),
        // reconcile first — reuse the stocked part so completion deducts stock,
        // and optionally update its price. Otherwise materialize a registry Part.
        if (inventoryEnabled) {
          // The pre-check is best-effort: if it fails, fall through to a plain
          // materialize so a network blip never blocks adding the part.
          try {
            const smRes = await axios.get(`/api/catalogue-items/${option.item_id}/stock-match/`);
            const { match, catalogue_prices: cataloguePrices } = smRes.data || {};
            if (isInventoryMode && match?.is_tracked && match.part) {
              // Inventory mode exists to START tracking: a code the workshop
              // already stocks gets the "already tracked" notice (match.part
              // carries stock_info), never the price-reconcile dialog.
              part = match.part;
            } else {
              const needsReconcile =
                match &&
                match.is_tracked &&
                (match.match_basis === 'code_tracked' || match.cost_differs || match.sell_differs);
              if (needsReconcile) {
                setReconcile({ option, match, cataloguePrices });
                return; // finally clears cataloguePicking; dialog drives the rest
              }
            }
          } catch {
            /* ignore — proceed with plain materialize below */
          }
        }
        if (!part) {
          const matRes = await axios.post(`/api/catalogue-items/${option.item_id}/materialize/`);
          part = matRes.data?.part;
        }
      }
      if (part) {
        setSelectedPart(part);
        setPartSearchInput(part.name || '');
      }
    } catch {
      setError(t('catalogues.materializeFailed'));
    } finally {
      setCataloguePicking(false);
    }
  };

  const handlePartChange = (e, value) => {
    if (value && value.__catalogue) {
      selectCatalogueItem(value);
      return;
    }
    setSelectedPart(value);
  };

  const handleBarcodeScan = useCallback(async (barcode) => {
    setScannerOpen(false);
    try {
      const params = new URLSearchParams({ search: barcode, typeahead: 'true' });
      if (onlyTrackedParts) params.set('has_inventory', 'true');
      if (codeOnly) params.set('code_only', 'true');
      const response = await axios.get(`/api/parts/?${params.toString()}`);
      const results = response.data.results || response.data;
      const resultList = Array.isArray(results) ? results : [];

      if (resultList.length === 1) {
        setSelectedPart(resultList[0]);
      } else if (resultList.length === 0) {
        setBarcodeSnackbar({ open: true, message: t('barcode.notFound'), severity: 'warning' });
      } else {
        setPartSearchInput(barcode);
        triggerPartsSearch(barcode);
        setBarcodeSnackbar({ open: true, message: t('barcode.multipleFound', { count: resultList.length }), severity: 'info' });
      }
    } catch {
      setBarcodeSnackbar({ open: true, message: t('barcode.searchError'), severity: 'error' });
    }
  }, [onlyTrackedParts, codeOnly, t, triggerPartsSearch]);

  const addExistingPart = async () => {
    if (!selectedPart) return;
    setSubmitting(true);
    try {
      if (!isMaintenanceMode) {
        // Template / registry mode: no maintenance POST — hand the picked Part
        // back to the parent. (Pure registry mode never reaches here because the
        // picker UI is hidden, but the branch is safe either way.)
        if (onPartCreated) {
          onPartCreated(selectedPart);
        }
        setAddedPartsCount((prev) => prev + 1);
        setSelectedPart(null);
        setQuantity('');
        return;
      }
      const oldParts = maintenance.parts;
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_part/`, {
        part_id: selectedPart.id,
        quantity: parseFloat(quantity) || 1,
        awaiting_confirmation: awaitingConfirmation,
      });
      setMaintenance(response.data);
      setAddedPartsCount((prev) => prev + 1);
      // Track newly added part IDs
      const newIds = findNewPartIds(oldParts, response.data.parts);
      setAddedPartIds((prev) => [...prev, ...newIds]);
      setSelectedPart(null);
      setQuantity('');
    } catch (err) {
      setError(t('Failed to add part. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Inventory mode: start tracking for the picked (or just-materialized) Part.
  const startTrackingForSelected = async () => {
    if (!selectedPart) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`/api/parts/${selectedPart.id}/enable-inventory/`, {
        initial_quantity: trackInitialQuantity || '0',
        reorder_point: trackReorderPoint || null,
        shelf_location: trackShelfLocation || '',
      });
      if (onPartCreated) {
        onPartCreated(selectedPart);
      }
      setAddedPartsCount((prev) => prev + 1);
      setBarcodeSnackbar({
        open: true,
        message: t('inventory.trackingStarted', { name: selectedPart.name }),
        severity: 'success',
      });
      // The part is tracked now, so it must drop out of the search results.
      setSelectedPart(null);
      setPartSearchInput('');
      resetPartsSearch();
      setCatalogueResults([]);
      setTrackInitialQuantity('0');
      setTrackShelfLocation('');
      setTrackReorderPoint('');
    } catch (err) {
      // The endpoint answers with a translated message under `error` (e.g. the
      // part got tracked meanwhile); a validation dict or a 403 `detail` gets the
      // generic text. Never the exception itself.
      console.error('Failed to start inventory tracking:', err);
      const backendMessage = err.response?.data?.error;
      setError(typeof backendMessage === 'string' ? backendMessage : t('inventory.startTrackingFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Manual part creation handlers
  // ─────────────────────────────────────────────────────────────

  const handleNewPartChange = (field) => (e) => {
    setNewPart({ ...newPart, [field]: e.target.value });
  };

  const createAndAddPart = async () => {
    if (!newPart.name.trim()) {
      setError(t('Part name is required'));
      return;
    }

    // Validate margin VAT scheme: purchase price is required when enabled
    if (newPart.is_margin_vat_scheme && !newPart.margin_vat_purchase_price) {
      setError(t('Purchase price required for margin VAT scheme'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const oldParts = isMaintenanceMode ? maintenance.parts : [];
      const isMargin = newPart.is_margin_vat_scheme;

      // For margin VAT: purchase price is in margin_vat_purchase_price, sell price is gross
      // For normal: regular VAT conversion logic

      // Get entered purchase price (not used for margin VAT parts)
      const enteredPrice = !isMargin && newPart.price ? parseFloat(newPart.price) : null;

      // Calculate VAT-exclusive purchase price if needed
      let priceExclVat = enteredPrice;
      if (enteredPrice !== null && priceIncludesVat) {
        priceExclVat = calcPriceExclVat(enteredPrice, DEFAULT_VAT_RATE);
      }

      // Get entered sell price (a lone "-" parses to NaN -> treat as not entered)
      const parsedSellPrice = parseFloat(newPart.sell_price);
      const enteredSellPrice = Number.isFinite(parsedSellPrice) ? parsedSellPrice : null;

      // Calculate VAT-exclusive sell price if needed (not for margin VAT — sell price is gross)
      let sellPriceExclVat = enteredSellPrice;
      if (!isMargin && enteredSellPrice !== null && sellPriceIncludesVat) {
        sellPriceExclVat = calcPriceExclVat(enteredSellPrice, DEFAULT_VAT_RATE);
      }

      // Create part with VAT preference
      const payload = {
        name: newPart.name,
        unit: newPart.unit || 'kpl',
        description: newPart.description,
        manufacturer_code: newPart.manufacturer_code,
        manufacturer_name: newPart.manufacturer_name,
        quantity: newPart.quantity,
        price: isMargin ? null : priceExclVat,
        vat_rate: DEFAULT_VAT_RATE,
        // Store VAT preference so it persists when editing (not relevant for margin VAT)
        stored_price_incl_vat: !isMargin && priceIncludesVat && enteredPrice !== null ? Math.round(enteredPrice * 100) / 100 : null,
        price_entered_with_vat: !isMargin && priceIncludesVat && enteredPrice !== null && enteredPrice > 0,
        // Sell price fields — for margin VAT, sell price is gross (customer price)
        sell_price: isMargin ? enteredSellPrice : sellPriceExclVat,
        stored_sell_price_incl_vat: isMargin
          ? (enteredSellPrice !== null ? Math.round(enteredSellPrice * 100) / 100 : null)
          : (sellPriceIncludesVat && enteredSellPrice !== null ? Math.round(enteredSellPrice * 100) / 100 : null),
        sell_price_entered_with_vat: isMargin
          ? (enteredSellPrice !== null && enteredSellPrice > 0)
          : (sellPriceIncludesVat && enteredSellPrice !== null && enteredSellPrice !== 0),
        source_type: 'manual',
        // Margin VAT scheme fields
        is_margin_vat_scheme: isMargin,
        margin_vat_purchase_price: isMargin
          ? parseFloat(newPart.margin_vat_purchase_price)
          : null,
        // Inventory tracking fields
        ...(enableTracking && {
          enable_inventory_tracking: true,
          initial_quantity: initialQuantity || '0',
          inventory_shelf_location: shelfLocation,
        }),
        // Hide from registry (maintenance-only part)
        ...(hideFromRegistry && { hide_from_registry: true }),
      };

      const createResponse = await axios.post('/api/parts/', payload);
      const createdPart = createResponse.data;

      if (isMaintenanceMode) {
        const response = await axios.patch(`/api/maintenances/${maintenance.id}/add_part/`, {
          part_id: createdPart.id,
          quantity: parseFloat(newPart.quantity) || 1,
          awaiting_confirmation: awaitingConfirmation,
        });
        setMaintenance(response.data);
        // Track newly added part IDs
        const newIds = findNewPartIds(oldParts, response.data.parts);
        setAddedPartIds((prev) => [...prev, ...newIds]);
      } else {
        if (onPartCreated) {
          onPartCreated(createdPart);
        }
      }
      setAddedPartsCount((prev) => prev + 1);

      // Reset form
      setNewPart({
        name: '',
        unit: 'kpl',
        description: '',
        price: '',
        sell_price: '',
        manufacturer_code: '',
        manufacturer_name: '',
        quantity: '',
        is_margin_vat_scheme: false,
        margin_vat_purchase_price: '',
      });
      setPriceIncludesVat(orgDefaultVat);
      setSellPriceIncludesVat(orgDefaultVat);
      setEnableTracking(manualOnly || isInventoryMode);
      setInitialQuantity('0');
      setShelfLocation('');
      if ((isMaintenanceMode || isInventoryMode) && !manualOnly) {
        setShowManualForm(false);
      }
    } catch (err) {
      setError(t('Failed to save part. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const isNewPartValid = () => {
    return newPart.name?.trim();
  };

  // ─────────────────────────────────────────────────────────────
  // Pending suggestion handlers
  // ─────────────────────────────────────────────────────────────

  const importSuggestion = async (suggestionId, duplicateAction, existingPartId) => {
    setProcessingIds((prev) => new Set([...prev, suggestionId]));

    try {
      const oldParts = isMaintenanceMode ? maintenance.parts : [];
      // Find the suggestion to get edited values
      const suggestion = pendingSuggestions.find((s) => s.id === suggestionId);
      const payload = {
        ...(isMaintenanceMode && maintenance?.id && { maintenance_id: maintenance.id }),
        ...(isMaintenanceMode && { awaiting_confirmation: awaitingConfirmation }),
        ...(hideFromRegistry && { hide_from_registry: true }),
        // Include editable fields in case user modified them
        ...(suggestion && {
          name: suggestion.name,
          description: suggestion.description,
          price: suggestion.price,
          manufacturer_code: suggestion.manufacturer_code,
          manufacturer_name: suggestion.manufacturer_name,
          price_includes_vat: suggestion.price_includes_vat || false,
          vat_rate: suggestion.vat_rate || 0,
          ...(suggestion.sell_price != null && { sell_price: suggestion.sell_price }),
          ...(isMaintenanceMode && { quantity: parseFloat(suggestion.quantity) || 1 }),
        }),
        // Inventory tracking fields
        ...(suggestion?.enable_inventory_tracking && {
          enable_inventory_tracking: true,
          initial_quantity: suggestion.initial_quantity || '0',
          inventory_shelf_location: suggestion.inventory_shelf_location || '',
        }),
        // Duplicate resolution fields
        ...(duplicateAction && { duplicate_action: duplicateAction }),
        ...(existingPartId && { existing_part_id: existingPartId }),
        organization: getOrganizationId(),
      };
      const importRes = await axios.post(`/api/parts/email-suggestions/${suggestionId}/import/`, payload);

      // Clear edits and conflict for this suggestion
      delete userEditsRef.current[suggestionId];
      if (suggestionConflict?.suggestion_id === suggestionId) {
        setSuggestionConflict(null);
      }

      if (isMaintenanceMode) {
        // Refresh maintenance and suggestions
        const maintenanceRes = await axios.get(`/api/maintenances/${maintenance.id}/`);
        setMaintenance(maintenanceRes.data);
        // Track newly added part IDs
        const newIds = findNewPartIds(oldParts, maintenanceRes.data.parts);
        setAddedPartIds((prev) => [...prev, ...newIds]);
      } else if (onPartCreated && importRes.data?.part) {
        onPartCreated(importRes.data.part);
      }
      setAddedPartsCount((prev) => prev + 1);
      await fetchPendingSuggestions();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.status === 'conflict') {
        // Duplicate conflict — show resolver
        setSuggestionConflict({
          ...err.response.data,
          suggestion_id: suggestionId,
        });
      } else {
        setError(t('Failed to import suggestion. Please try again.'));
      }
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(suggestionId);
        return next;
      });
    }
  };

  const importAllSuggestions = async (suggestions) => {
    for (const suggestion of suggestions) {
      if (processingIds.has(suggestion.id)) continue;
      await importSuggestion(suggestion.id);
    }
  };

  const declineSuggestion = async (suggestionId) => {
    setProcessingIds((prev) => new Set([...prev, suggestionId]));

    try {
      await axios.post(`/api/parts/email-suggestions/${suggestionId}/decline/`);
      delete userEditsRef.current[suggestionId];
      await fetchPendingSuggestions();
    } catch (err) {
      setError(t('Operation failed. Please try again.'));
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(suggestionId);
        return next;
      });
    }
  };

  const handleSuggestionExpandToggle = (suggestionId) => {
    setExpandedSuggestions((prev) => ({
      ...prev,
      [suggestionId]: !prev[suggestionId],
    }));
  };

  const handleSuggestionEdit = (suggestionId, field, value) => {
    // Store edit in ref to preserve during polling
    userEditsRef.current[suggestionId] = {
      ...userEditsRef.current[suggestionId],
      [field]: value,
    };
    setPendingSuggestions((prev) =>
      prev.map((s) => (s.id === suggestionId ? { ...s, [field]: value } : s))
    );
  };

  const handleSuggestionVatToggle = (id, field = 'price') => {
    setPendingSuggestions(prev => prev.map(s => {
      if (s.id !== id) return s;
      const flagKey = field === 'price' ? 'price_includes_vat' : 'sell_price_includes_vat';
      const wasIncl = s[flagKey];
      const currentPrice = parseFloat(s[field]) || 0;
      const vatRate = parseFloat(s.vat_rate) || DEFAULT_VAT_RATE;
      const newPrice = wasIncl ? calcPriceExclVat(currentPrice, vatRate) : calcPriceInclVat(currentPrice, vatRate);
      const updates = {
        [flagKey]: !wasIncl,
        [field]: Math.round(newPrice * 100) / 100,
      };
      // Store in ref to preserve during polling
      userEditsRef.current[id] = { ...userEditsRef.current[id], ...updates };
      return { ...s, ...updates };
    }));
  };

  // ─────────────────────────────────────────────────────────────
  // Extraction handlers (paste, file upload)
  // ─────────────────────────────────────────────────────────────

  const pollForResult = async (taskId, maxAttempts = 60) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const statusResponse = await axios.get(`/api/parts/extraction-status/${taskId}/`);

      if (statusResponse.data.status === 'success') {
        return statusResponse.data;
      } else if (statusResponse.data.status === 'failed') {
        throw new Error(statusResponse.data.error || 'Extraction failed');
      }
    }
    throw new Error('Extraction timed out');
  };

  const extractFromUrl = async (urlToExtract) => {
    if (!urlToExtract.trim()) return;

    setExtracting(true);
    setError(null);
    setExtractedParts([]);
    setEventId(null);
    resetBatchStates();

    try {
      const response = await axios.post('/api/parts/extract-from-url/', {
        url: urlToExtract.trim(),
        maintenance_id: maintenance?.id,
        organization: getOrganizationId(),
      });

      const taskId = response.data.task_id;
      const result = await pollForResult(taskId);

      const extractedPartsList = result.extracted_parts || [];
      setExtractedParts(transformExtractedParts(extractedPartsList));

      if (result.event_id) {
        setEventId(result.event_id);
      }

      if (extractedPartsList.length > 0) {
        setPasteContent('');
      }
    } catch (err) {
      setError(t('Part extraction failed. Please try again or add the part manually.'));
    } finally {
      setExtracting(false);
    }
  };

  const extractFromClipboard = async () => {
    if (!pasteContent.trim() && !pastedImage) return;

    if (!pastedImage && isUrl(pasteContent)) {
      return extractFromUrl(pasteContent);
    }

    setExtracting(true);
    setError(null);
    setExtractedParts([]);
    resetBatchStates();

    try {
      const payload = {
        content_type: pastedImage ? 'image' : 'text',
        content: pastedImage ? pastedImage.base64 : pasteContent.trim(),
        maintenance_id: maintenance?.id,
      };

      const response = await axios.post('/api/parts/extract-from-clipboard/', payload);

      const extractedPartsList = response.data.extracted_parts || [];
      setExtractedParts(transformExtractedParts(extractedPartsList));

      if (extractedPartsList.length > 0) {
        setPasteContent('');
        setPastedImage(null);
      }
    } catch (err) {
      setError(t('Part extraction failed. Please try again or add the part manually.'));
    } finally {
      setExtracting(false);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setExtracting(true);
      setError(null);
      setExtractedParts([]);
      setEventId(null);
      resetBatchStates();

      try {
        const formData = new FormData();
        formData.append('file', file);
        if (maintenance?.id) {
          formData.append('maintenance_id', maintenance.id);
        }
        formData.append('organization', getOrganizationId());

        const response = await axios.post('/api/parts/extract-from-file/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        const extractedPartsList = response.data.extracted_parts || [];
        setExtractedParts(transformExtractedParts(extractedPartsList));

        if (response.data.event_id) {
          setEventId(response.data.event_id);
        }
      } catch (err) {
        setError(t('Part extraction failed. Please try again or add the part manually.'));
      } finally {
        setExtracting(false);
      }
    },
    [maintenance?.id]
  );

  const handlePaste = useCallback((event) => {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          const reader = new FileReader();
          // onloadend also fires after a failed read, with a null result: on Android
          // Chrome the clipboard file can become unreadable once the paste event is over
          // (Sentry VERSTAANI-FRONTEND-13). Only a string result is an image.
          reader.onload = () => {
            const dataUrl = reader.result;
            if (typeof dataUrl !== 'string' || !dataUrl.includes(',')) {
              setError(t('Part extraction failed. Please try again or add the part manually.'));
              return;
            }
            setPastedImage({ preview: dataUrl, base64: dataUrl.split(',')[1] });
            setPasteContent('');
          };
          reader.onerror = () => {
            setError(t('Part extraction failed. Please try again or add the part manually.'));
          };
          reader.readAsDataURL(blob);
        }
        return;
      }
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
    disabled: hideImportTools,
  });

  const clearPastedImage = () => {
    setPastedImage(null);
  };

  // ─────────────────────────────────────────────────────────────
  // Extracted parts handlers
  // ─────────────────────────────────────────────────────────────

  const handleExpandToggle = (index) => {
    setExpandedExtractedParts((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Transform AI extraction results to show original document price with VAT metadata
  const transformExtractedParts = (partsList) => {
    return partsList.map(part => ({
      ...part,
      // Show original document price when VAT-inclusive, otherwise keep the VAT-exclusive price
      price: part.original_price_included_vat && part.original_price ? part.original_price : part.price,
      price_includes_vat: part.original_price_included_vat || false,
      vat_rate: part.vat_rate || 0,
    }));
  };

  const handlePartEdit = (index, field, value) => {
    setExtractedParts((prev) =>
      prev.map((part, i) => (i === index ? { ...part, [field]: value } : part))
    );
    // Any edit clears all conflicts — next save will re-check
    if (extractedConflicts.length > 0) {
      setExtractedConflicts([]);
    }
  };

  const handlePartVatToggle = (index, field = 'price') => {
    setExtractedParts(prev => prev.map((part, i) => {
      if (i !== index) return part;
      const isPrice = field === 'price';
      const flagKey = isPrice ? 'price_includes_vat' : 'sell_price_includes_vat';
      const wasIncl = part[flagKey];
      const currentPrice = parseFloat(part[field]) || 0;
      const vatRate = parseFloat(part.vat_rate) || DEFAULT_VAT_RATE;
      const newPrice = wasIncl ? calcPriceExclVat(currentPrice, vatRate) : calcPriceInclVat(currentPrice, vatRate);
      return {
        ...part,
        [flagKey]: !wasIncl,
        [field]: Math.round(newPrice * 100) / 100,
      };
    }));
  };

  const handleRemoveExtractedPart = (index) => {
    setExtractedParts((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Batch action: VAT interpretation swap ---
  // Keeps the displayed price unchanged, just toggles the price_includes_vat flag.
  // The backend converts to VAT-exclusive on save based on this flag.
  const handleVatSwap = () => {
    setExtractedParts((prev) =>
      prev.map((part) => ({
        ...part,
        price_includes_vat: !part.price_includes_vat,
      }))
    );
    setVatSwapped((prev) => !prev);
  };

  // --- Batch action: Price type swap (purchase <-> sell) ---
  const handlePriceTypeSwap = () => {
    setExtractedParts((prev) =>
      prev.map((part) => {
        if (priceIsSellPrice) {
          return { ...part, price: part.sell_price, sell_price: null, price_includes_vat: part.sell_price_includes_vat || false };
        } else {
          return { ...part, sell_price: part.price, price: null, sell_price_includes_vat: part.price_includes_vat || false };
        }
      })
    );
    setPriceIsSellPrice((prev) => !prev);
  };

  // --- Batch action: Translate names ---
  const handleTranslateNames = async () => {
    const names = extractedParts.map((p) => p.name);
    if (names.length === 0) return;

    setTranslatingNames(true);
    setError(null);

    try {
      const lang = i18n.language || 'fi';
      const response = await axios.post('/api/parts/translate-names/', {
        names,
        target_language: lang.substring(0, 2),
      });

      const translations = response.data.translations;
      setExtractedParts((prev) =>
        prev.map((part, i) => {
          const translated = translations[i];
          if (!translated || translated === part.name) return part;
          const originalNote = `(${part.name})`;
          const desc = part.description
            ? `${originalNote} ${part.description}`
            : originalNote;
          return { ...part, name: translated, description: desc };
        })
      );
      setNamesTranslated(true);
    } catch (err) {
      setError(t('Translation failed. Please try again.'));
    } finally {
      setTranslatingNames(false);
    }
  };

  const resetBatchStates = () => {
    setVatSwapped(false);
    setPriceIsSellPrice(false);
    setTranslatingNames(false);
    setNamesTranslated(false);
  };

  const saveExtractedParts = async () => {
    const partsToSave = filterByVehicle
      ? displayedPartsWithIndex.map(({ part }) => part)
      : extractedParts;
    if (partsToSave.length === 0) return;

    setSaving(true);
    setError(null);

    try {
      const oldParts = isMaintenanceMode ? maintenance.parts : [];
      const response = await axios.post('/api/parts/save-extracted/', {
        parts: partsToSave,
        ...(isMaintenanceMode && { maintenance_id: maintenance?.id }),
        ...(isMaintenanceMode && { awaiting_confirmation: awaitingConfirmation }),
        ...(hideFromRegistry && { hide_from_registry: true }),
        event_id: eventId,
        organization: getOrganizationId(),
      });

      const { status: responseStatus, conflicts, exact_matches } = response.data;
      const matchCount = (exact_matches || []).length;

      if (responseStatus === 'has_conflicts') {
        // Some parts had conflicts — keep only conflicting parts and re-index
        const rawConflicts = conflicts || [];
        const conflictIndices = new Set(rawConflicts.map((c) => c.index));
        setExtractedParts((prev) => prev.filter((_, i) => conflictIndices.has(i)));
        // Re-index conflicts to match the new extractedParts array (0, 1, 2, ...)
        const reindexed = rawConflicts.map((c, newIdx) => ({ ...c, index: newIdx }));
        setExtractedConflicts(reindexed);
        setExactMatchCount((prev) => prev + matchCount);
      } else {
        // All saved successfully
        setExtractedParts([]);
        setEventId(null);
        setPasteContent('');
        setExtractedConflicts([]);
        setExactMatchCount((prev) => prev + matchCount);
      }

      if (isMaintenanceMode) {
        const maintenanceRes = await axios.get(`/api/maintenances/${maintenance.id}/`);
        setMaintenance(maintenanceRes.data);
        const newIds = findNewPartIds(oldParts, maintenanceRes.data.parts);
        setAddedPartIds((prev) => [...prev, ...newIds]);
      } else if (onPartCreated) {
        const savedParts = response.data.created_parts || [];
        savedParts.forEach((p) => onPartCreated(p));
      }

      // Count saved parts (total minus conflicts)
      const savedCount = partsToSave.length - (conflicts || []).length;
      if (savedCount > 0) {
        setAddedPartsCount((prev) => prev + savedCount);
      }
    } catch (err) {
      setError(t('Failed to save parts. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const resolveExtractedConflict = async (conflict, action) => {
    setResolvingConflict(true);
    setError(null);
    try {
      const partData = {
        ...conflict.incoming_part,
        duplicate_action: action,
        existing_part_id: conflict.existing_part.id,
      };
      const oldParts = isMaintenanceMode ? maintenance.parts : [];
      const resolveRes = await axios.post('/api/parts/save-extracted/', {
        parts: [partData],
        ...(isMaintenanceMode && { maintenance_id: maintenance?.id }),
        ...(isMaintenanceMode && { awaiting_confirmation: awaitingConfirmation }),
        ...(hideFromRegistry && { hide_from_registry: true }),
        event_id: eventId,
        organization: getOrganizationId(),
      });

      // Remove resolved conflict and its part row (match by manufacturer_code, not array index)
      const resolvedCode = conflict.incoming_part?.manufacturer_code;
      setExtractedConflicts((prev) => prev.filter((c) => c.incoming_part?.manufacturer_code !== resolvedCode));
      setExtractedParts((prev) => prev.filter((p) => p.manufacturer_code !== resolvedCode));
      setAddedPartsCount((prev) => prev + 1);

      if (isMaintenanceMode) {
        const maintenanceRes = await axios.get(`/api/maintenances/${maintenance.id}/`);
        setMaintenance(maintenanceRes.data);
        const newIds = findNewPartIds(oldParts, maintenanceRes.data.parts);
        setAddedPartIds((prev) => [...prev, ...newIds]);
      } else if (onPartCreated) {
        const savedParts = resolveRes.data?.created_parts || [];
        savedParts.forEach((p) => onPartCreated(p));
      }
    } catch (err) {
      setError(t('Failed to save part. Please try again.'));
    } finally {
      setResolvingConflict(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Settings handlers
  // ─────────────────────────────────────────────────────────────

  const copyInboxEmail = () => {
    navigator.clipboard.writeText(inboxEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ─────────────────────────────────────────────────────────────
  // Dialog close and cancel handlers
  // ─────────────────────────────────────────────────────────────

  const handleDialogClose = () => {
    handleClose();
  };

  const handleCancelClick = () => {
    if (isMaintenanceMode && addedPartIds.length > 0) {
      // Show confirmation dialog
      setShowCancelConfirm(true);
    } else {
      // No parts added or registry mode, just close
      handleClose();
    }
  };

  const confirmCancelAndRemoveParts = async () => {
    setCancelling(true);
    try {
      // Remove all parts added during this session
      for (const partId of addedPartIds) {
        await axios.delete(`/api/maintenances/${maintenance.id}/remove_part/${partId}/`);
      }
      // Refresh maintenance
      const maintenanceRes = await axios.get(`/api/maintenances/${maintenance.id}/`);
      setMaintenance(maintenanceRes.data);
      setShowCancelConfirm(false);
      handleClose();
    } catch (err) {
      setError(t('Operation failed. Please try again.'));
      setShowCancelConfirm(false);
    } finally {
      setCancelling(false);
    }
  };

  const cancelWithoutRemovingParts = () => {
    setShowCancelConfirm(false);
    handleClose();
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          width: { xs: '100%', md: '600px' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {isInventoryMode
            ? t('inventory.addPartTitle')
            : isRegistryMode || manualOnly ? t('Add part to registry') : t('Add part')}
        </Typography>
        <IconButton onClick={handleDialogClose} size="small" data-testid="close-dialog-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 0, pt: 0 }} {...getRootProps()}>
        <input {...getInputProps()} />

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mx: { xs: 2, sm: 3 }, mt: 1, mb: 0, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        {!hideImportTools && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => { setActiveTab(newValue); setSuggestionConflict(null); }}
            variant="fullWidth"
            sx={{
              minHeight: '48px',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: '48px',
              },
            }}
          >
            <Tab label={t('Add part')} />
            {isMaintenanceMode && <Tab label={t('supplierParts.tabLabel')} />}
            <Tab
              label={
                <Badge
                  badgeContent={pendingSuggestions.length}
                  color="warning"
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -12,
                      top: 2,
                    },
                  }}
                >
                  {t('Suggestions')}
                </Badge>
              }
            />
          </Tabs>
        </Box>
        )}

        {/* ─────────────────────────────────────────────────────────── */}
        {/* TAB 1: Direct Input */}
        {/* ─────────────────────────────────────────────────────────── */}
        <Box
          role="tabpanel"
          hidden={activeTab !== 0}
          sx={{ px: { xs: 2, sm: 3 }, pt: 4 }}
        >
          {activeTab === 0 && (
            <>
              {/* Registry Search — maintenance and template modes only; hidden in pure registry-add mode */}
        {showRegistryPicker && (
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
            }}
          >
            <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'text.secondary',
              }}
            >
              {catalogueActive ? t('catalogues.searchPartsAndCatalogues') : t('Search from parts registry')}
            </Typography>
          </Box>
          {isInventoryMode && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} data-testid="inventory-search-hint">
              {catalogueEnabled ? t('inventory.searchToTrackHint') : t('inventory.searchToTrackHintRegistry')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
            {/* Inventory mode already excludes tracked parts, so the shelf chip has nothing to narrow. */}
            {inventoryEnabled && !catalogueFilterActive && !isInventoryMode && (
              <Chip
                icon={<InventoryIcon />}
                label={t('inventory.onlyTrackedParts')}
                onClick={() => setOnlyTrackedParts(!onlyTrackedParts)}
                color={onlyTrackedParts ? 'primary' : 'default'}
                variant={onlyTrackedParts ? 'filled' : 'outlined'}
                size="medium"
                data-testid="parts-search-shelf-only"
              />
            )}
            {/* Restrict the search to part codes — a workshop-wide preference,
                so it shows regardless of inventory tracking or catalogue filters. */}
            <Tooltip title={t('partSearch.codeOnlyHint')}>
              <Chip
                icon={<TagIcon />}
                label={t('partSearch.codeOnly')}
                onClick={() => setCodeOnly(!codeOnly)}
                color={codeOnly ? 'primary' : 'default'}
                variant={codeOnly ? 'filled' : 'outlined'}
                size="medium"
                data-testid="parts-search-code-only"
              />
            </Tooltip>
          </Box>
          {/* Restrict the search to specific vendor catalogues (multi-select).
              None selected = search registry + all catalogues. */}
          {catalogueEnabled && catalogueSources.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                {t('catalogues.filterBySource')}
              </Typography>
              {catalogueSources.map((src) => {
                const selected = selectedCatalogueIds.includes(src.id);
                return (
                  <Chip
                    key={src.id}
                    label={src.vendor_name || src.name}
                    onClick={() =>
                      setSelectedCatalogueIds((prev) =>
                        prev.includes(src.id) ? prev.filter((x) => x !== src.id) : [...prev, src.id]
                      )
                    }
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    size="medium"
                    data-testid={`catalogue-filter-${src.id}`}
                  />
                );
              })}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <FormControl fullWidth>
            <Autocomplete
              options={combinedOptions}
              value={selectedPart}
              onChange={handlePartChange}
              inputValue={partSearchInput}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText={t('No options')}
              loadingText={t('Loading...')}
              {...partsAutocompleteProps}
              loading={partsAutocompleteProps.loading || cataloguePicking}
              onOpen={() => {
                // Catalogue-filter mode shows a preview on open (the registry list is
                // already preloaded; catalogue browse loads here). No-op when no filter.
                if (catalogueFilterActive) runCatalogueSearch(partSearchInput);
              }}
              onInputChange={(event, value, reason) => {
                // Effect-driven reset (no event) of a pre-filled term: keep the term.
                if (reason === 'reset' && !event && value === '' && prefillRef.current) return;
                if (reason === 'input') prefillRef.current = '';
                setPartSearchInput(value);
                if (reason === 'input') {
                  // When a catalogue filter is active the registry isn't searched.
                  if (!catalogueFilterActive) triggerPartsSearch(value);
                  runCatalogueSearch(value);
                }
              }}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (option.__catalogue) {
                  return option.name || option.source_code || '';
                }
                let label = option.name;
                const extras = [];
                if (option.manufacturer_code) extras.push(option.manufacturer_code);
                const buyPrice = option.is_margin_vat_scheme
                  ? option.margin_vat_purchase_price
                  : orgDefaultVat ? (option.price_incl_vat ?? option.price) : option.price;
                const sellPrice = option.is_margin_vat_scheme
                  ? option.sell_price
                  : orgDefaultVat ? (option.sell_price_incl_vat ?? option.sell_price) : option.sell_price;
                if (buyPrice && sellPrice) {
                  extras.push(`${t('Buy')}: ${formatPriceWithCurrency(buyPrice)}`);
                  extras.push(`${t('Sell')}: ${formatPriceWithCurrency(sellPrice)}`);
                } else if (buyPrice) {
                  extras.push(formatPriceWithCurrency(buyPrice));
                } else if (sellPrice) {
                  extras.push(formatPriceWithCurrency(sellPrice));
                }
                if (extras.length > 0) {
                  label += ` (${extras.join(' | ')})`;
                }
                return label;
              }}
              renderOption={(props, option) => {
                if (option.__catalogue) {
                  const deposit = parseFloat(option.deposit);
                  const stock = parseFloat(option.total_stock);
                  // Catalogue prices are stored ex-VAT; respect the org's VAT-inclusive
                  // display setting exactly like registry parts do (a picked catalogue
                  // item materializes into a Part at the standard VAT rate). Sell-price-
                  // only feeds (e.g. Avant) carry no cost, so show their OVH instead of
                  // mislabelling it "Ostohinta".
                  const hasCost = option.purchase_price != null;
                  const catRawPrice = hasCost ? option.purchase_price : option.list_price;
                  const catPriceLabel = hasCost ? t('catalogues.purchasePrice') : t('catalogues.ovhLabel');
                  const catBuyPrice =
                    catRawPrice == null ? null : orgDefaultVat ? calcPriceInclVat(catRawPrice) : catRawPrice;
                  const catVatLabel = orgDefaultVat ? t('incl. VAT') : t('excl. VAT');
                  return (
                    <Box component="li" {...props} key={option.id} data-testid={`catalogue-option-${option.item_id}`}>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip
                              label={t('catalogues.priceList')}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '10px' }}
                            />
                            {option.name || option.source_code}
                            {option.source_code && (
                              <Typography component="span" variant="body2" color="text.secondary">
                                [{option.source_code}]
                              </Typography>
                            )}
                            {deposit > 0 && (
                              <Chip
                                label={`${t('catalogues.deposit')} ${formatPriceWithCurrency(option.deposit)}`}
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ height: 18, fontSize: '10px' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[
                              option.vendor_name,
                              option.brand,
                              catBuyPrice != null
                                ? `${catPriceLabel}: ${formatPriceWithCurrency(catBuyPrice)} (${catVatLabel})`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </Typography>
                        </Box>
                        {stock > 0 && (
                          <Tooltip
                            title={
                              option.last_imported_at
                                ? t('catalogues.stockTooltipDated', { date: formatDate(option.last_imported_at) })
                                : t('catalogues.stockTooltip')
                            }
                          >
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: 'text.disabled', flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                              {stock}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                }
                const stockInfo = option.stock_info;
                const stockQty = stockInfo ? parseFloat(stockInfo.quantity_free) : null;
                const buyPrice = option.is_margin_vat_scheme
                  ? option.margin_vat_purchase_price
                  : orgDefaultVat ? (option.price_incl_vat ?? option.price) : option.price;
                const sellPrice = option.is_margin_vat_scheme
                  ? option.sell_price
                  : orgDefaultVat ? (option.sell_price_incl_vat ?? option.sell_price) : option.sell_price;
                const vatLabel = option.is_margin_vat_scheme
                  ? t('Margin VAT')
                  : orgDefaultVat ? t('incl. VAT') : t('excl. VAT');
                return (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {option.name}
                          {(option.primary_code || option.manufacturer_code) && (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                              [{option.primary_code || option.manufacturer_code}]
                            </Typography>
                          )}
                          {/* A part sold by the litre/kilo next to its package twin: the
                              unit is what tells "MOOTTORIÖLJY 5W-30" (l) from the barrel. */}
                          {option.unit && option.unit !== 'kpl' && (
                            <Chip
                              label={option.unit}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1, height: 18, fontSize: '10px' }}
                              data-testid="part-option-unit"
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            buyPrice && sellPrice
                              ? `${t('Buy')}: ${formatPriceWithCurrency(buyPrice)} • ${t('Sell')}: ${formatPriceWithCurrency(sellPrice)} (${vatLabel})`
                              : buyPrice
                                ? `${t('Buy')}: ${formatPriceWithCurrency(buyPrice)} (${vatLabel})`
                                : sellPrice
                                  ? `${t('Sell')}: ${formatPriceWithCurrency(sellPrice)} (${vatLabel})`
                                  : null,
                            option.vendor_name,
                            option.manufacturer_name,
                          ].filter(Boolean).join(' • ')}
                        </Typography>
                      </Box>
                      {inventoryEnabled && stockInfo && (
                        <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                          <Chip
                            size="small"
                            label={`${formatQuantity(stockInfo.quantity_free)} ${t('inventory.pcsShort')}`}
                            color={
                              stockInfo.is_negative ? 'error' :
                              stockInfo.is_low_stock ? 'warning' :
                              stockQty > 0 ? 'success' : 'default'
                            }
                            variant="outlined"
                            sx={{ height: 20, fontSize: '11px' }}
                          />
                          {stockInfo.shelf_location && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, fontSize: '10px' }}>
                              {stockInfo.shelf_location}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                  placeholder={t('Search by name, code, vendor...')}
                  size="small"
                  inputRef={partSearchInputRef}
                  inputProps={{ ...params.inputProps, 'data-testid': 'part-search-input' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      transition: 'all 0.15s ease',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#cbd5e1',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                      },
                    },
                  }}
                />
              )}
            />
          </FormControl>
          {showBarcodeScanner && (
            <Tooltip title={t('barcode.scanBarcode')}>
              <IconButton
                onClick={() => setScannerOpen(true)}
                sx={{
                  mt: '1px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '10px',
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                }}
              >
                <QrCodeScannerIcon />
              </IconButton>
            </Tooltip>
          )}
          </Box>

          {/* Inventory mode: a pick starts tracking instead of adding to a work order */}
          {selectedPart && isInventoryMode && (
            selectedPart.stock_info ? (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: '10px' }} data-testid="inventory-already-tracked">
                {t('inventory.alreadyTracked', { name: selectedPart.name })}
              </Alert>
            ) : (
              <Box
                sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: 1.5 }}
                data-testid="inventory-start-tracking-panel"
              >
                <Typography variant="body2" fontWeight={500}>
                  {selectedPart.name}
                  {(selectedPart.primary_code || selectedPart.manufacturer_code) && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, ...codeFont }}>
                      {selectedPart.primary_code || selectedPart.manufacturer_code}
                    </Typography>
                  )}
                </Typography>
                <DecimalField
                  label={t('inventory.initialQuantity')}
                  value={trackInitialQuantity}
                  onChange={setTrackInitialQuantity}
                  size="small"
                  fullWidth
                  helperText={t('inventory.initialQuantityHelp')}
                  inputProps={{ 'data-testid': 'inventory-track-initial-quantity' }}
                />
                <TextField
                  label={t('inventory.shelfLocation')}
                  value={trackShelfLocation}
                  onChange={(e) => setTrackShelfLocation(e.target.value)}
                  size="small"
                  fullWidth
                  helperText={t('inventory.shelfLocationHelp')}
                  inputProps={{ 'data-testid': 'inventory-track-shelf-location' }}
                />
                <DecimalField
                  label={t('inventory.reorderPoint')}
                  value={trackReorderPoint}
                  onChange={setTrackReorderPoint}
                  size="small"
                  fullWidth
                  helperText={t('inventory.reorderPointHelp')}
                  inputProps={{ 'data-testid': 'inventory-track-reorder-point' }}
                />
                <Button
                  variant="contained"
                  color="success"
                  onClick={startTrackingForSelected}
                  disabled={submitting || trackInitialQuantity === ''}
                  startIcon={submitting ? <CircularProgress size={18} /> : <InventoryIcon />}
                  sx={{ borderRadius: '10px', alignSelf: 'flex-start' }}
                  data-testid="inventory-start-tracking-button"
                >
                  {t('inventory.startTracking')}
                </Button>
              </Box>
            )
          )}

          {/* Quick add button for selected part */}
          {selectedPart && !isInventoryMode && (
            <>
              {/* Stock info for selected part */}
              {inventoryEnabled && selectedPart.stock_info && (() => {
                const addQty = parseFloat(quantity) || 1;
                const currentStock = parseFloat(selectedPart.stock_info.quantity_in_stock);
                const projectedStock = currentStock - addQty;
                return (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        {t('inventory.quantityInStock')}: <strong>{formatQuantity(selectedPart.stock_info.quantity_in_stock)}</strong>
                      </Typography>
                      {selectedPart.stock_info.shelf_location && (
                        <Chip icon={<PlaceIcon />} label={selectedPart.stock_info.shelf_location} size="small" variant="outlined" />
                      )}
                    </Box>
                    {projectedStock < 0 && (
                      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1, py: 0.5 }}>
                        {t('inventory.stockInsufficient')}
                      </Alert>
                    )}
                  </Box>
                );
              })()}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{t('Quantity')}:</Typography>
                  <DecimalField
                    value={quantity}
                    onChange={setQuantity}
                    placeholder="1"
                    size="small"
                    sx={{ width: '80px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="success"
                  onClick={addExistingPart}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={18} /> : <AddIcon />}
                  sx={{ borderRadius: '10px' }}
                  data-testid="add-selected-part-button"
                >
                  {t('Add')}
                </Button>
              </Box>
            </>
          )}
        </Box>
        )}

        {/* Manual form toggle — only when a picker is present (not in pure registry-add) */}
        {showRegistryPicker && (
        <Box
          onClick={() => setShowManualForm(!showManualForm)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            color: '#3b82f6',
            mb: showManualForm ? 1.5 : 0,
            py: 0.75,
            transition: 'color 0.15s ease',
            '&:hover': { color: '#2563eb' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
            {t('Create new part manually')}
          </Typography>
        </Box>
        )}

        {/* Manual form */}
        <Collapse in={showManualForm}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: '12px',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  label={t('Part Name')}
                  value={newPart.name}
                  onChange={handleNewPartChange('name')}
                  required
                  fullWidth
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <TextField
                  label={t('part_unit')}
                  value={newPart.unit}
                  onChange={(e) => setNewPart({ ...newPart, unit: e.target.value })}
                  select
                  size="small"
                  sx={{ minWidth: 110, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                >
                  {UNIT_OPTIONS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u} — {t('unit_' + u)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <TextField
                label={t('Description')}
                value={newPart.description}
                onChange={(e) => setNewPart({ ...newPart, description: e.target.value.slice(0, 500) })}
                fullWidth
                multiline
                rows={2}
                size="small"
                inputProps={{ maxLength: 500 }}
                helperText={`${newPart.description.length}/500`}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField
                  label={t('Manufacturer')}
                  value={newPart.manufacturer_name}
                  onChange={handleNewPartChange('manufacturer_name')}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <TextField
                  label={t('Part Code')}
                  value={newPart.manufacturer_code}
                  onChange={handleNewPartChange('manufacturer_code')}
                  fullWidth
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 1.5 }}>
                {!newPart.is_margin_vat_scheme && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1 }}>
                    {canSeeCosts && (
                      <DecimalField
                        label={priceIncludesVat ? t('Purchase price (incl. VAT)') : t('Purchase price (excl. VAT)')}
                        value={newPart.price}
                        onChange={(value) => setNewPart({ ...newPart, price: value })}
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                    )}
                    <VatModeToggle
                      value={priceIncludesVat ? 'incl' : 'excl'}
                      onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                      sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                    />
                  </Box>
                  <VatConverseLine
                    value={newPart.price}
                    mode={priceIncludesVat ? 'incl' : 'excl'}
                    vatRate={DEFAULT_VAT_RATE}
                  />
                </Box>
                )}
                {isMaintenanceMode && (
                <DecimalField
                  label={t('Quantity')}
                  value={newPart.quantity}
                  onChange={(value) => setNewPart({ ...newPart, quantity: value })}
                  placeholder="1"
                  size="small"
                  sx={{ width: { xs: '100%', sm: '100px' }, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                )}
              </Box>

              {/* Default Sell Price — hidden for margin VAT, replaced by Customer price */}
              {newPart.is_margin_vat_scheme ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <DecimalField
                  label={t('Customer price')}
                  value={newPart.sell_price}
                  disabled={!canEditPrices}
                  onChange={(value) => setNewPart({ ...newPart, sell_price: value })}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
                <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontStyle: 'italic' }}>
                  {t('VAT is calculated on profit margin (margin VAT scheme)')}
                </Typography>
              </Box>
              ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1 }}>
                  <DecimalField
                    label={sellPriceIncludesVat ? t('Default sell price (incl. VAT)') : t('Default sell price (excl. VAT)')}
                    value={newPart.sell_price}
                    disabled={!canEditPrices}
                    onChange={(value) => setNewPart({ ...newPart, sell_price: value })}
                    allowNegative
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                  <VatModeToggle
                    value={sellPriceIncludesVat ? 'incl' : 'excl'}
                    onChange={(e, val) => val && setSellPriceIncludesVat(val === 'incl')}
                    sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                    help={false}
                  />
                </Box>
                <VatConverseLine
                  value={newPart.sell_price}
                  mode={sellPriceIncludesVat ? 'incl' : 'excl'}
                  vatRate={DEFAULT_VAT_RATE}
                />
                <Typography sx={{ fontSize: '11px', color: 'text.disabled', fontStyle: 'italic' }}>
                  {t('Optional. If set, this price is used instead of margin calculation.')}
                </Typography>
              </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <InventoryTrackingFields
                inventoryEnabled={inventoryEnabled}
                enableTracking={enableTracking}
                setEnableTracking={setEnableTracking}
                initialQuantity={initialQuantity}
                setInitialQuantity={setInitialQuantity}
                shelfLocation={shelfLocation}
                setShelfLocation={setShelfLocation}
              />

              {/* Margin VAT scheme section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newPart.is_margin_vat_scheme}
                      onChange={(e) => setNewPart((prev) => ({
                        ...prev,
                        is_margin_vat_scheme: e.target.checked,
                        // Clear purchase price when disabled
                        margin_vat_purchase_price: e.target.checked ? prev.margin_vat_purchase_price : '',
                      }))}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                          {t('Margin VAT scheme (used parts)')}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                          {t('VAT is calculated only on the profit margin, not the full price')}
                        </Typography>
                      </Box>
                      <HelpButton
                        text={t('Margin scheme help text')}
                        title={t('Margin VAT scheme')}
                        size={16}
                      />
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />

                <Collapse in={newPart.is_margin_vat_scheme}>
                  <Box sx={{ pl: 2, pt: 1 }}>
                    <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '8px' }}>
                      {t('Margin scheme legal warning')}
                    </Alert>
                    {canSeeCosts && (
                      <DecimalField
                        label={t('Purchase price paid')}
                        value={newPart.margin_vat_purchase_price}
                        onChange={(value) => setNewPart((prev) => ({ ...prev, margin_vat_purchase_price: value }))}
                        fullWidth
                        size="small"
                        required
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                    )}
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.5 }}>
                      {t('Enter the purchase price including VAT. VAT will be calculated on the margin.')}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={createAndAddPart}
                disabled={!isNewPartValid() || submitting}
                sx={{
                  borderRadius: '10px',
                  bgcolor: 'primary.main',
                  mt: 1,
                }}
              >
                {submitting ? <CircularProgress size={20} /> : <span>{isInventoryMode ? (enableTracking ? t('inventory.createAndTrack') : t('Add to registry')) : isRegistryMode || manualOnly ? t('Add to registry') : t('Create and add')}</span>}
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {!hideImportTools && (
        <>
        {/* ─────────────────────────────────────────────────────────── */}
        {/* Divider before import zone */}
        {/* ─────────────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            my: 3.5,
          }}
        >
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'text.disabled',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}
          >
            {t('or import automatically')}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        </Box>

        {/* ─────────────────────────────────────────────────────────── */}
        {/* ZONE 3: Import (Paste/File) */}
        {/* ─────────────────────────────────────────────────────────── */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
            }}
          >
            <ContentPasteIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'text.secondary',
              }}
            >
              {t('Paste part data')}
            </Typography>
            <HelpButton text={t('pastePartDataHelp')} size={16} />
          </Box>

          {/* Pasted image preview */}
          {pastedImage && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1.5,
                border: 1,
                borderColor: 'secondary.main',
                borderRadius: '10px',
                bgcolor: 'background.default',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <img
                  src={pastedImage.preview}
                  alt={t('Pasted screenshot')}
                  style={{
                    maxWidth: '120px',
                    maxHeight: '80px',
                    borderRadius: '8px',
                    objectFit: 'contain',
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('Screenshot ready')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Click Extract to analyze the image')}
                  </Typography>
                </Box>
                <IconButton onClick={clearPastedImage} size="small" sx={{ color: 'error.main' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          )}

          {/* Text paste area */}
          {!pastedImage && (
            <Box
              sx={{
                mb: 1.75,
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: '10px',
                bgcolor: '#f8fafc',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: 'grey.500',
                  bgcolor: 'background.paper',
                },
                '&:focus-within': {
                  borderColor: '#3b82f6',
                  borderStyle: 'solid',
                  bgcolor: 'background.paper',
                  boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
              }}
            >
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder={t('Paste text, table data, URL, or image (Ctrl+V) here...')}
                value={pasteContent}
                onChange={(e) => setPasteContent(e.target.value)}
                onPaste={handlePaste}
                disabled={extracting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    border: 'none',
                    bgcolor: 'transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    lineHeight: 1.5,
                    '&::placeholder': {
                      color: '#94a3b8',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={extractFromClipboard}
              disabled={extracting || (!pasteContent.trim() && !pastedImage)}
              startIcon={extracting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                flex: 1,
                borderRadius: '9px',
                py: 1.25,
                fontWeight: 600,
                fontSize: '14px',
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  bgcolor: '#e2e8f0',
                  color: '#94a3b8',
                },
              }}
            >
              {extracting ? t('Extracting...') : t('Extract data')}
            </Button>
            <FilePickerButton
              onFiles={onDrop}
              accept={{
                'image/jpeg': ['.jpg', '.jpeg'],
                'image/png': ['.png'],
                'image/webp': ['.webp'],
                'application/pdf': ['.pdf'],
              }}
              disabled={extracting}
              buttonProps={{
                variant: 'outlined',
                startIcon: <CloudUploadIcon />,
                sx: {
                  flex: 1,
                  borderRadius: '9px',
                  py: 1.25,
                  fontWeight: 600,
                  fontSize: '14px',
                  borderColor: '#e2e8f0',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: '#cbd5e1',
                    color: 'text.primary',
                  },
                },
              }}
            >
              {t('Upload file')}
            </FilePickerButton>
          </Box>

          {/* Drag active overlay */}
          {isDragActive && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(25, 118, 210, 0.1)',
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
              }}
            >
              <Typography color="primary" sx={{ fontWeight: 500 }}>
                {t('Drop file here')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Extracted parts results */}
        {extractedParts.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: '12px',
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                {extractedParts.length} {t('parts recognized')}
              </Typography>
            </Box>

            {/* Vehicle filter checkbox */}
            {isMaintenanceMode && hasVehicleIdentifier && hasPartsWithVehicleInfo && (
              <Box sx={{ px: 2, pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filterByVehicle}
                        onChange={(e) => setFilterByVehicle(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '13px' }}>
                        {t('filterByVehicleLabel')}
                      </Typography>
                    }
                    sx={{ mr: 0.5 }}
                  />
                  <HelpButton text={t('filterByVehicleHelp')} />
                </Box>
                {filterByVehicle && displayedPartsWithIndex.length !== extractedParts.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                    {t('showingFilteredParts', { shown: displayedPartsWithIndex.length, total: extractedParts.length })}
                  </Typography>
                )}
              </Box>
            )}

            {/* AI extraction warning */}
            <Alert severity="warning" sx={{ mx: 2, mt: 1.5, borderRadius: '8px' }}>
              <Typography variant="body2">
                {t('This data has been extracted using AI and may contain errors. Always verify the correctness of the information before saving.')}
              </Typography>
            </Alert>

            {/* Batch actions toolbar */}
            <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1.5, flexWrap: 'wrap' }}>
              <Tooltip title={t('vatSwapTooltip')} arrow enterDelay={500}>
                <Button
                  size="small"
                  variant={vatSwapped ? 'contained' : 'outlined'}
                  color={vatSwapped ? 'warning' : 'inherit'}
                  startIcon={<SwapHorizIcon />}
                  onClick={handleVatSwap}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px' }}
                >
                  {t('Swap VAT interpretation')}
                </Button>
              </Tooltip>
              <Tooltip title={t('sellPriceTooltip')} arrow enterDelay={500}>
                <Button
                  size="small"
                  variant={priceIsSellPrice ? 'contained' : 'outlined'}
                  color={priceIsSellPrice ? 'info' : 'inherit'}
                  startIcon={<SellIcon />}
                  onClick={handlePriceTypeSwap}
                  sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px' }}
                >
                  {priceIsSellPrice ? t('Sell prices (OVH)') : t('Purchase prices')}
                </Button>
              </Tooltip>
              <Tooltip title={t('translateNamesTooltip')} arrow enterDelay={500}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={translatingNames ? <CircularProgress size={14} /> : <TranslateIcon />}
                    onClick={handleTranslateNames}
                    disabled={translatingNames || namesTranslated}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '12px' }}
                  >
                    {translatingNames ? t('Translating...') : namesTranslated ? t('Names translated') : t('Translate names')}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {displayedPartsWithIndex.map(({ part, originalIndex }) => (
              <Box
                key={originalIndex}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                {/* Desktop layout */}
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', px: 2, py: 1.5, gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: '14px' }} noWrap>
                        {part.name}
                      </Typography>
                      <Tooltip title={`${t('Confidence')}: ${Math.round(part.confidence * 100)}%`}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: getConfidenceColor(part.confidence),
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    </Box>
                    {(part.manufacturer_name || part.primary_code || part.manufacturer_code) && (
                      <Typography variant="caption" color="text.secondary">
                        {part.manufacturer_name}
                        {(part.primary_code || part.manufacturer_code) && ` - ${part.primary_code || part.manufacturer_code}`}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isMaintenanceMode && (
                    <Typography variant="body2" color="text.secondary">
                      {part.quantity || 1} {t('pcs')}
                    </Typography>
                    )}
                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                      {formatPrice(part.price)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {part.price_includes_vat ? t('incl. VAT') : t('excl. VAT')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleExpandToggle(originalIndex)}>
                      {expandedExtractedParts[originalIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleRemoveExtractedPart(originalIndex)} sx={{ color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Mobile layout */}
                <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 2, py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                          {part.name}
                        </Typography>
                        <Tooltip title={`${t('Confidence')}: ${Math.round(part.confidence * 100)}%`}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: getConfidenceColor(part.confidence),
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      </Box>
                      {(part.primary_code || part.manufacturer_code) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {part.primary_code || part.manufacturer_code}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => handleExpandToggle(originalIndex)}>
                        {expandedExtractedParts[originalIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <IconButton size="small" onClick={() => handleRemoveExtractedPart(originalIndex)} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75 }}>
                    {isMaintenanceMode && (
                    <Typography variant="body2" color="text.secondary">
                      {part.quantity || 1} {t('pcs')}
                    </Typography>
                    )}
                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                      {formatPrice(part.price)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {part.price_includes_vat ? t('incl. VAT') : t('excl. VAT')}
                    </Typography>
                  </Box>
                </Box>

                {/* Expanded edit view */}
                <Collapse in={expandedExtractedParts[originalIndex]}>
                  <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        label={t('Name')}
                        size="small"
                        fullWidth
                        value={part.name}
                        onChange={(e) => handlePartEdit(originalIndex, 'name', e.target.value)}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {canSeeCosts && (
                          <DecimalField
                            label={part.price_includes_vat ? t('Purchase price (incl. VAT)') : t('Purchase price (excl. VAT)')}
                            size="small"
                            value={part.price ?? ''}
                            onChange={(value) => handlePartEdit(originalIndex, 'price', value)}
                            sx={{ flex: 1 }}
                          />
                        )}
                        <VatModeToggle
                          value={part.price_includes_vat ? 'incl' : 'excl'}
                          onChange={(e, val) => val && handlePartVatToggle(originalIndex, 'price')}
                          sx={{ flexShrink: 0 }}
                          help={false}
                        />
                        {isMaintenanceMode && (
                        <DecimalField
                          label={t('Quantity')}
                          size="small"
                          value={part.quantity ?? ''}
                          onChange={(value) => handlePartEdit(originalIndex, 'quantity', value)}
                          placeholder="1"
                          sx={{ width: '100px', flexShrink: 0 }}
                        />
                        )}
                      </Box>
                      <VatConverseLine
                        value={part.price}
                        mode={part.price_includes_vat ? 'incl' : 'excl'}
                        vatRate={parseFloat(part.vat_rate) || DEFAULT_VAT_RATE}
                        sx={{ mt: -1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DecimalField
                          label={part.sell_price_includes_vat ? t('Sell price (incl. VAT)') : t('Sell price (excl. VAT)')}
                          size="small"
                          value={part.sell_price ?? ''}
                          disabled={!canEditPrices}
                          onChange={(value) => handlePartEdit(originalIndex, 'sell_price', value)}
                          allowNegative
                          sx={{ flex: 1 }}
                        />
                        <VatModeToggle
                          value={part.sell_price_includes_vat ? 'incl' : 'excl'}
                          onChange={(e, val) => val && handlePartVatToggle(originalIndex, 'sell_price')}
                          sx={{ flexShrink: 0 }}
                          help={false}
                        />
                      </Box>
                      <VatConverseLine
                        value={part.sell_price}
                        mode={part.sell_price_includes_vat ? 'incl' : 'excl'}
                        vatRate={parseFloat(part.vat_rate) || DEFAULT_VAT_RATE}
                        sx={{ mt: -1 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                        <TextField
                          label={t('Manufacturer')}
                          size="small"
                          fullWidth
                          value={part.manufacturer_name || ''}
                          onChange={(e) => handlePartEdit(originalIndex, 'manufacturer_name', e.target.value)}
                        />
                        <TextField
                          label={t('Code')}
                          size="small"
                          fullWidth
                          value={part.manufacturer_code || ''}
                          onChange={(e) => handlePartEdit(originalIndex, 'manufacturer_code', e.target.value)}
                        />
                      </Box>
                      <InventoryTrackingFields
                        inventoryEnabled={inventoryEnabled}
                        enableTracking={part.enable_inventory_tracking || false}
                        setEnableTracking={(val) => handlePartEdit(originalIndex, 'enable_inventory_tracking', val)}
                        initialQuantity={part.initial_quantity || '0'}
                        setInitialQuantity={(val) => handlePartEdit(originalIndex, 'initial_quantity', val)}
                        shelfLocation={part.inventory_shelf_location || ''}
                        setShelfLocation={(val) => handlePartEdit(originalIndex, 'inventory_shelf_location', val)}
                      />
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            ))}

            {extractedConflicts.length === 0 && (
              <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={saveExtractedParts}
                  disabled={saving || displayedPartsWithIndex.length === 0}
                  startIcon={saving ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                  sx={{
                    borderRadius: '10px',
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' },
                  }}
                >
                  {saving
                    ? t('Saving...')
                    : `${t('Save')} ${displayedPartsWithIndex.length} ${displayedPartsWithIndex.length === 1 ? t('part') : t('parts')}`}
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* Exact match info */}
        {exactMatchCount > 0 && extractedParts.length === 0 && extractedConflicts.length === 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '10px' }}>
            {t('partDuplicate.exactMatchesUsed', { count: exactMatchCount })}
          </Alert>
        )}

        {/* Conflicts requiring resolution */}
        {extractedConflicts.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: 1,
              borderColor: 'warning.main',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'warning.dark' }}>
                {t('partDuplicate.conflictsFound', { count: extractedConflicts.length })}
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {extractedConflicts.map((conflict) => (
                <DuplicatePartResolver
                  key={conflict.incoming_part?.manufacturer_code || conflict.index}
                  existingPart={conflict.existing_part}
                  incomingPart={conflict.incoming_part}
                  differences={conflict.differences}
                  onUseExisting={() => resolveExtractedConflict(conflict, 'use_existing')}
                  onUpdateExisting={() => resolveExtractedConflict(conflict, 'update_existing')}
                  disabled={resolvingConflict}
                />
              ))}
            </Box>
          </Paper>
        )}

        </>
        )}

            </>
          )}
        </Box>

        {/* ─────────────────────────────────────────────────────────── */}
        {/* TAB: Supplier Parts (maintenance mode only) */}
        {/* ─────────────────────────────────────────────────────────── */}
        {isMaintenanceMode && (
          <Box role="tabpanel" hidden={activeTab !== 1} sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>
            {activeTab === 1 && (
              <SupplierPartsTab
                maintenance={maintenance}
                awaitingConfirmation={awaitingConfirmation}
                hideFromRegistry={hideFromRegistry}
                setMaintenance={setMaintenance}
                onPartsImported={(count, newIds) => {
                  setAddedPartsCount((prev) => prev + count);
                  setAddedPartIds((prev) => [...prev, ...newIds]);
                }}
                onError={setError}
                handleClose={handleClose}
              />
            )}
          </Box>
        )}

        {/* ─────────────────────────────────────────────────────────── */}
        {/* TAB: Suggestions from Email + Extension */}
        {/* ─────────────────────────────────────────────────────────── */}
        <Box
          role="tabpanel"
          hidden={activeTab !== suggestionsTabIndex}
          sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}
        >
          {activeTab === suggestionsTabIndex && (
            <>
              {/* Suggestions list (if any) */}
              {pendingSuggestions.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 3,
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {t('No pending suggestions')}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t('Suggestions from email and browser extension will appear here')}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Vehicle filter checkbox for suggestions */}
                  {isMaintenanceMode && hasVehicleIdentifier && hasSuggestionsWithVehicleInfo && (
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={filterSuggestionsByVehicle}
                              onChange={(e) => setFilterSuggestionsByVehicle(e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '13px' }}>
                              {t('filterByVehicleLabel')}
                            </Typography>
                          }
                          sx={{ mr: 0.5 }}
                        />
                        <HelpButton text={t('filterByVehicleHelp')} />
                      </Box>
                      {filterSuggestionsByVehicle && filteredSuggestions.length !== pendingSuggestions.length && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                          {t('showingFilteredParts', { shown: filteredSuggestions.length, total: pendingSuggestions.length })}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* AI extraction warning */}
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                    <Typography variant="body2">
                      {t('This data has been extracted using AI and may contain errors. Always verify the correctness of the information before saving.')}
                    </Typography>
                  </Alert>
                  {suggestionGroups.map((group, groupIndex) => (
                    <Paper
                      key={groupIndex}
                      elevation={0}
                      sx={{
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '10px',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Group header */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1.5,
                          bgcolor: group.sourceType === 'email' ? '#f0f7ff' : '#faf5ff',
                          borderBottom: 1,
                          borderColor: 'divider',
                        }}
                      >
                        {group.sourceType === 'email' ? (
                          <EmailIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
                        ) : (
                          <ExtensionIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '13px' }} noWrap>
                            {group.eventSubject}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {group.sourceLabel} • {group.suggestions.length} {t('parts')}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => importAllSuggestions(group.suggestions)}
                          disabled={group.suggestions.every((s) => processingIds.has(s.id))}
                          sx={{ minWidth: 'auto', px: 1.5, borderRadius: '8px', whiteSpace: 'nowrap' }}
                        >
                          {t('partDuplicate.addAll')}
                        </Button>
                      </Box>

                      {/* Batch actions for this group */}
                      <Box sx={{ display: 'flex', gap: 0.5, px: 2, py: 1, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
                        <Tooltip title={t('vatSwapTooltip')} arrow enterDelay={500}>
                          <Button
                            size="small"
                            variant={vatSwapped ? 'contained' : 'outlined'}
                            color={vatSwapped ? 'warning' : 'inherit'}
                            startIcon={<SwapHorizIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => {
                              const ids = new Set(group.suggestions.map(s => s.id));
                              setPendingSuggestions(prev => prev.map(s => {
                                if (!ids.has(s.id)) return s;
                                const updated = { ...s, price_includes_vat: !s.price_includes_vat };
                                userEditsRef.current[s.id] = { ...(userEditsRef.current[s.id] || {}), price_includes_vat: updated.price_includes_vat };
                                return updated;
                              }));
                              setVatSwapped(prev => !prev);
                            }}
                            sx={{ borderRadius: '6px', textTransform: 'none', fontSize: '11px', py: 0.25, minHeight: 0 }}
                          >
                            {t('Swap VAT interpretation')}
                          </Button>
                        </Tooltip>
                        <Tooltip title={t('sellPriceTooltip')} arrow enterDelay={500}>
                          <Button
                            size="small"
                            variant={priceIsSellPrice ? 'contained' : 'outlined'}
                            color={priceIsSellPrice ? 'info' : 'inherit'}
                            startIcon={<SellIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => {
                              const ids = new Set(group.suggestions.map(s => s.id));
                              setPendingSuggestions(prev => prev.map(s => {
                                if (!ids.has(s.id)) return s;
                                if (priceIsSellPrice) {
                                  const updated = { ...s, price: s.sell_price || s.price, sell_price: undefined };
                                  userEditsRef.current[s.id] = { ...(userEditsRef.current[s.id] || {}), price: updated.price, sell_price: undefined };
                                  return updated;
                                } else {
                                  const updated = { ...s, sell_price: s.price, price: 0 };
                                  userEditsRef.current[s.id] = { ...(userEditsRef.current[s.id] || {}), sell_price: updated.sell_price, price: 0 };
                                  return updated;
                                }
                              }));
                              setPriceIsSellPrice(prev => !prev);
                            }}
                            sx={{ borderRadius: '6px', textTransform: 'none', fontSize: '11px', py: 0.25, minHeight: 0 }}
                          >
                            {priceIsSellPrice ? t('Sell prices (OVH)') : t('Purchase prices')}
                          </Button>
                        </Tooltip>
                        <Tooltip title={t('translateNamesTooltip')} arrow enterDelay={500}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={translatingNames ? <CircularProgress size={12} /> : <TranslateIcon sx={{ fontSize: '14px !important' }} />}
                              onClick={async () => {
                                const names = group.suggestions.map(s => s.name);
                                if (names.length === 0) return;
                                setTranslatingNames(true);
                                try {
                                  const lang = i18n.language || 'fi';
                                  const res = await axios.post('/api/parts/translate-names/', { names, target_language: lang.substring(0, 2) });
                                  const translations = res.data.translations;
                                  const ids = group.suggestions.map(s => s.id);
                                  setPendingSuggestions(prev => prev.map(s => {
                                    const idx = ids.indexOf(s.id);
                                    if (idx === -1) return s;
                                    const translated = translations[idx];
                                    if (!translated || translated === s.name) return s;
                                    const desc = s.description ? `(${s.name}) ${s.description}` : `(${s.name})`;
                                    userEditsRef.current[s.id] = { ...(userEditsRef.current[s.id] || {}), name: translated, description: desc };
                                    return { ...s, name: translated, description: desc };
                                  }));
                                  setNamesTranslated(true);
                                } catch (err) {
                                  setError(t('Translation failed. Please try again.'));
                                } finally {
                                  setTranslatingNames(false);
                                }
                              }}
                              disabled={translatingNames || namesTranslated}
                              sx={{ borderRadius: '6px', textTransform: 'none', fontSize: '11px', py: 0.25, minHeight: 0 }}
                            >
                              {translatingNames ? t('Translating...') : namesTranslated ? t('Names translated') : t('Translate names')}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>

                      {/* Suggestions in this group */}
                      {group.suggestions.map((suggestion) => (
                        <Box
                          key={suggestion.id}
                          sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                          }}
                        >
                          {/* Desktop layout */}
                          <Box
                            sx={{
                              display: { xs: 'none', sm: 'flex' },
                              alignItems: 'center',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 500, fontSize: '14px' }} noWrap>
                                {suggestion.name}
                              </Typography>
                              {(suggestion.primary_code || suggestion.manufacturer_code) && (
                                <Typography variant="caption" color="text.secondary" sx={{ ...codeFont }}>
                                  {suggestion.primary_code || suggestion.manufacturer_code}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isMaintenanceMode && (suggestion.quantity && suggestion.quantity > 1) && (
                                <Typography variant="body2" color="text.secondary">
                                  {formatQuantity(suggestion.quantity)} {t('pcs')}
                                </Typography>
                              )}
                              <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {formatPrice(suggestion.price)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {suggestion.price_includes_vat ? t('incl. VAT') : t('excl. VAT')}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                              <IconButton size="small" onClick={() => handleSuggestionExpandToggle(suggestion.id)}>
                                {expandedSuggestions[suggestion.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => importSuggestion(suggestion.id)}
                                disabled={processingIds.has(suggestion.id)}
                                sx={{ minWidth: 'auto', px: 1.5, borderRadius: '8px' }}
                              >
                                {processingIds.has(suggestion.id) ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  t('Add')
                                )}
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => declineSuggestion(suggestion.id)}
                                disabled={processingIds.has(suggestion.id)}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Mobile layout */}
                          <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 2, py: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 500, fontSize: '14px', mb: 0.25 }}>
                                  {suggestion.name}
                                </Typography>
                                {(suggestion.primary_code || suggestion.manufacturer_code) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ...codeFont, display: 'block' }}>
                                    {suggestion.primary_code || suggestion.manufacturer_code}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <IconButton size="small" onClick={() => handleSuggestionExpandToggle(suggestion.id)}>
                                  {expandedSuggestions[suggestion.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => declineSuggestion(suggestion.id)}
                                  disabled={processingIds.has(suggestion.id)}
                                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {isMaintenanceMode && (suggestion.quantity && suggestion.quantity > 1) && (
                                  <Typography variant="body2" color="text.secondary">
                                    {formatQuantity(suggestion.quantity)} {t('pcs')}
                                  </Typography>
                                )}
                                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                  {formatPrice(suggestion.price)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {suggestion.price_includes_vat ? t('incl. VAT') : t('excl. VAT')}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => importSuggestion(suggestion.id)}
                                disabled={processingIds.has(suggestion.id)}
                                sx={{ minWidth: 'auto', px: 1.5, borderRadius: '8px' }}
                              >
                                {processingIds.has(suggestion.id) ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  t('Add')
                                )}
                              </Button>
                            </Box>
                          </Box>

                          {/* Expanded edit view */}
                          <Collapse in={expandedSuggestions[suggestion.id]}>
                            <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <TextField
                                  label={t('Name')}
                                  size="small"
                                  fullWidth
                                  value={suggestion.name || ''}
                                  onChange={(e) => handleSuggestionEdit(suggestion.id, 'name', e.target.value)}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DecimalField
                                    label={suggestion.price_includes_vat ? t('Purchase price (incl. VAT)') : t('Purchase price (excl. VAT)')}
                                    size="small"
                                    value={suggestion.price ?? ''}
                                    onChange={(value) => handleSuggestionEdit(suggestion.id, 'price', value)}
                                    sx={{ flex: 1 }}
                                  />
                                  <VatModeToggle
                                    value={suggestion.price_includes_vat ? 'incl' : 'excl'}
                                    onChange={(e, val) => val && handleSuggestionVatToggle(suggestion.id, 'price')}
                                    sx={{ flexShrink: 0 }}
                                    help={false}
                                  />
                                  {isMaintenanceMode && (
                                  <DecimalField
                                    label={t('Quantity')}
                                    size="small"
                                    value={suggestion.quantity ?? ''}
                                    onChange={(value) => handleSuggestionEdit(suggestion.id, 'quantity', value)}
                                    placeholder="1"
                                    sx={{ width: '100px', flexShrink: 0 }}
                                  />
                                  )}
                                </Box>
                                <VatConverseLine
                                  value={suggestion.price}
                                  mode={suggestion.price_includes_vat ? 'incl' : 'excl'}
                                  vatRate={parseFloat(suggestion.vat_rate) || DEFAULT_VAT_RATE}
                                  sx={{ mt: -1 }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                                  <TextField
                                    label={t('Manufacturer')}
                                    size="small"
                                    fullWidth
                                    value={suggestion.manufacturer_name || ''}
                                    onChange={(e) => handleSuggestionEdit(suggestion.id, 'manufacturer_name', e.target.value)}
                                  />
                                  <TextField
                                    label={t('Code')}
                                    size="small"
                                    fullWidth
                                    value={suggestion.manufacturer_code || ''}
                                    onChange={(e) => handleSuggestionEdit(suggestion.id, 'manufacturer_code', e.target.value)}
                                  />
                                </Box>
                                <InventoryTrackingFields
                                  inventoryEnabled={inventoryEnabled}
                                  enableTracking={suggestion.enable_inventory_tracking || false}
                                  setEnableTracking={(val) => handleSuggestionEdit(suggestion.id, 'enable_inventory_tracking', val)}
                                  initialQuantity={suggestion.initial_quantity || '0'}
                                  setInitialQuantity={(val) => handleSuggestionEdit(suggestion.id, 'initial_quantity', val)}
                                  shelfLocation={suggestion.inventory_shelf_location || ''}
                                  setShelfLocation={(val) => handleSuggestionEdit(suggestion.id, 'inventory_shelf_location', val)}
                                />
                              </Box>
                            </Box>
                          </Collapse>
                          {/* Inline duplicate conflict resolver for this suggestion */}
                          {suggestionConflict?.suggestion_id === suggestion.id && (
                            <Box sx={{ px: 2, pb: 2 }}>
                              <DuplicatePartResolver
                                existingPart={suggestionConflict.existing_part}
                                incomingPart={suggestion}
                                differences={suggestionConflict.differences}
                                onUseExisting={() => importSuggestion(
                                  suggestion.id,
                                  'use_existing',
                                  suggestionConflict.existing_part.id
                                )}
                                onUpdateExisting={() => importSuggestion(
                                  suggestion.id,
                                  'update_existing',
                                  suggestionConflict.existing_part.id
                                )}
                                disabled={processingIds.has(suggestion.id)}
                              />
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Paper>
                  ))}
                </>
              )}

              {/* Import settings - collapsible accordion */}
              <Accordion
                expanded={settingsExpanded}
                onChange={(e, expanded) => setSettingsExpanded(expanded)}
                elevation={0}
                sx={{
                  mt: 2,
                  bgcolor: 'grey.50',
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                    borderRadius: '12px',
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '&:hover': { bgcolor: 'grey.100' },
                    borderRadius: settingsExpanded ? '12px 12px 0 0' : '12px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                      {t('Import settings')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                  {/* Email inbox */}
                  <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <EmailIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {t('Email import')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {t('Send order confirmations and quotes to this address:')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: 'background.paper',
                        borderRadius: '8px',
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        sx={{
                          flex: 1,
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {inboxEmail}
                      </Typography>
                      <Tooltip title={copied ? t('Copied!') : t('Copy to clipboard')}>
                        <IconButton size="small" onClick={copyInboxEmail}>
                          {copied ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                      {t('emailImportWhitelistNote')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        handleClose();
                        navigate('/settings?tab=integrations');
                      }}
                      sx={{ borderRadius: '8px', fontSize: '12px' }}
                    >
                      {t('Manage allowed senders')}
                    </Button>
                  </Box>

                  {/* Extension */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <ExtensionIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                        {t('Browser extension')}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      {t('Capture parts directly from supplier websites.')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        handleClose();
                        navigate('/settings?tab=integrations');
                      }}
                      sx={{ borderRadius: '8px', fontSize: '12px' }}
                    >
                      {t('Install Chrome extension')}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>

        {/* Awaiting confirmation checkbox - outside tabs (maintenance mode only) */}
        {isMaintenanceMode && (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 1.5,
            mt: 1,
            borderTop: '1px solid',
            borderColor: '#f1f5f9',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: 'text.disabled', mb: 0.5 }}>
            {t('checkboxesAffectAllParts')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={awaitingConfirmation}
                  onChange={(e) => setAwaitingConfirmation(e.target.checked)}
                  sx={{
                    color: 'warning.main',
                    '&.Mui-checked': { color: 'warning.main' },
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  {t('Awaiting customer confirmation')}
                </Typography>
              }
              sx={{ mr: 0.5 }}
            />
            <HelpButton text={t('awaitingConfirmationHelp')} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideFromRegistry}
                  onChange={(e) => setHideFromRegistry(e.target.checked)}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  {t("Don't add to parts registry")}
                </Typography>
              }
              sx={{ mr: 0.5 }}
            />
            <HelpButton text={t('hideFromRegistryHelp')} />
          </Box>
        </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          justifyContent: 'space-between',
          bgcolor: '#f8fafc',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          {addedPartsCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Typography sx={{ color: 'success.main', fontWeight: 500, fontSize: '14px' }}>
                {addedPartsCount} {addedPartsCount === 1 ? t('part added') : t('parts added')}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleCancelClick}
            sx={{
              borderRadius: '9px',
              px: 2.5,
              fontWeight: 600,
              fontSize: '14px',
              borderColor: '#e2e8f0',
              color: 'text.secondary',
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: '#f1f5f9',
                borderColor: '#cbd5e1',
              },
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogClose}
            disabled={addedPartsCount === 0}
            sx={{
              borderRadius: '9px',
              px: 3,
              fontWeight: 600,
              fontSize: '14px',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: '#e2e8f0',
                color: '#a1a1aa',
              },
            }}
          >
            {t('Done')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>

    {/* Cancel confirmation dialog (maintenance mode only) */}
    {isMaintenanceMode && (
    <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{t('Remove added parts?')}</DialogTitle>
      <DialogContent>
        <Typography>
          {t('Do you want to remove the parts you added to this work order?')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={cancelWithoutRemovingParts}
          sx={{ borderRadius: '10px' }}
        >
          {t('No, keep them')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={confirmCancelAndRemoveParts}
          disabled={cancelling}
          startIcon={cancelling ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ borderRadius: '10px' }}
        >
          {t('Yes, remove')}
        </Button>
      </DialogActions>
    </Dialog>
    )}

    <BarcodeScannerDialog
      open={scannerOpen}
      onClose={() => setScannerOpen(false)}
      onScan={handleBarcodeScan}
    />

    {reconcile && (
      <CatalogueStockReconcileDialog
        open
        option={reconcile.option}
        match={reconcile.match}
        cataloguePrices={reconcile.cataloguePrices}
        onResolved={(part) => {
          setReconcile(null);
          if (part) {
            setSelectedPart(part);
            setPartSearchInput(part.name || '');
          }
        }}
        onClose={() => setReconcile(null)}
      />
    )}

    <Snackbar
      open={barcodeSnackbar.open}
      autoHideDuration={4000}
      onClose={() => setBarcodeSnackbar((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setBarcodeSnackbar((prev) => ({ ...prev, open: false }))}
        severity={barcodeSnackbar.severity}
        sx={{ borderRadius: '10px' }}
      >
        {barcodeSnackbar.message}
      </Alert>
    </Snackbar>
  </>
  );
}

export default AddPartDialog;
